<template>
  <form ref="form" class="ortic-form-horizontal">
    <o-card v-if="item" :capExceed="item.cap_exceeded ? true : false"
            :title="$t('sandoz.educational_contract') + ' // ' + (item.contract_no ? item.contract_no : '-')">
      <b-tabs class="inline-tabs" ref="tabs" v-model="activeTab">
        <b-tab :title="$t('sandoz.general')" active>

          <b-row>
            <b-col>
              <o-value-select v-model="item.user_id" :label="$t('sandoz.owner')"
                              :readonly="readonly" text-field="name"
                              url="/api/system/users?filter%5Bactive%5D=1%25&sortBy=name&include=flm,approval_roles,regions"
                              value-field="id" @input-object="setCurrentUser"></o-value-select>
              <b-alert show variant="warning" v-if="currentUser && item.region_id === null">{{$t('sandoz.no_region_user')}}</b-alert>                
              <o-value-select v-model="item.type" :items="contractTypes" :label="$t('sandoz.type')"
                              :readonly="readonly || !!item.id"/>

              <o-form-group :label="$t('sandoz.customer_external_id')" class="mb-2">
                <b-input-group>
                  <input v-model="item.customer_external_id" class="form-control" disabled type="text">
                  <b-input-group-append v-if="!readonly">
                    <b-btn variant="primary" @click.prevent="setMasterAddress">
                      <fa icon="ellipsis-h"/>
                    </b-btn>
                  </b-input-group-append>
                </b-input-group>
              </o-form-group>

              <o-form-group :label="$t('sandoz.contract_year')">
                <b-form-select v-model="item.contract_year" :options="years" :readonly="readonly" :disabled="readonly">
                </b-form-select>
              </o-form-group>

              <o-datepicker v-model="item.contract_start" :disabled="readonly"
                            :errorField="'contract_start'" :label="$t('sandoz.contract_start')"
                            :typeable="true"/>
              <o-datepicker v-model="item.contract_expiration" :disabled="readonly"
                            :errorField="'contract_expiration'" :label="$t('sandoz.contract_expiration')" :typeable="true"
                            class="d-none"/>
              <o-form-group :label="$t('sandoz.previous_contract')" class="mb-2 d-none">
                <b-input-group>
                  <input :value="item.previous && item.previous.contract_no ? item.previous.contract_no : null"
                         class="form-control" disabled type="text">
                  <b-input-group-append>
                    <b-btn variant="primary" @click.prevent="$refs.addPreviousContractModel.show()">
                      <fa icon="ellipsis-h"/>
                    </b-btn>
                  </b-input-group-append>
                  <input v-model="item.previous_contract" type="hidden">
                </b-input-group>
              </o-form-group>

              <o-value-select v-model="item.language" :items="languages" :label="$t('sandoz.language')"
                              :readonly="checkLangSelection(item) ? 'readonly' : null"/>
            </b-col>
            <b-col>
              <o-input v-model="item.no_of_medical_practices" :errorField="'no_of_medical_practices'"
                       :ignore-range="true" :label="['APO_EDU', 'KAM_APO_EDU'].includes(item.type) ? $t('sandoz.no_of_pharmacists') : $t('sandoz.no_of_doctors')" :readonly="readonly" :step="1"
                       format="integer" type="number" :class="checkMinDoctor() ? 'has-error': ''" :description="$t('sandoz.min_3')"/>
                       
              <o-input v-model="item.total_training_budget" :errorField="'total_training_budget'"
              :ignore-range="true"
              :readonly="item.status != 'Draft'"
              :label="$t('sandoz.total_training_budget')" :step="1" format="integer" type="number"></o-input>


              <o-input v-model="item.max_threshold_per_hcp" :errorField="'max_threshold_per_hcp'"
                       :ignore-range="true" :label="['APO_EDU', 'KAM_APO_EDU'].includes(item.type) ? $t('sandoz.support_per_pharmacist') : $t('sandoz.support_per_doctor')" :readonly="readonly" :step="1"
                       format="integer" type="number" :class="checkMaxThreshold() ? 'has-error': ''" :description="['APO_EDU', 'KAM_APO_EDU'].includes(item.type) ? $t('sandoz.support_pharmacist_description') : $t('sandoz.support_doctor_description')" />

              <o-input v-model="item.amount_of_educational" :errorField="'amount_of_educational'"
                       :ignore-range="true"
                       :readonly="true"
                       :label="$t('sandoz.amount_of_educational')" :step="1" format="integer" type="number"
                       :class="checkBudget() ? 'has-error': ''" :description="['APO_EDU', 'KAM_APO_EDU'].includes(item.type) ? $t('sandoz.amount_education_pharmacist_description') : $t('sandoz.amount_education_description')"></o-input>
                       
              <o-input v-model="item.planned_amount_of_educational" :errorField="'planned_amount_of_educational'"
              :ignore-range="true"
              :label="$t('sandoz.planned_amount_of_educational')" :step="1" format="integer" type="number" 
              :class="checkPlannedAmount() ? 'has-error': ''" :description="formatDescription(item.plan_target_invest)" :descriptionClass="calculatePlanTargetInvest()"></o-input>

              <o-form-group :label="$t('sandoz.actual_amount_of_educational')" class="mb-2" 
                :class="{ 'has-error': checkAmount(), 'd-none': item.status === 'Draft' }">
                <b-input-group>
                  <input v-model="item.actual_amount_of_educational" class="form-control" type="number">
                  <b-input-group-append v-if="item.status == 'Committed' || item.status == 'Approved' || item.status == 'Signed'">
                    <b-btn variant="primary" @click.prevent="setActualEducation(item)">
                      {{  $t('core.save') }}
                    </b-btn>
                  </b-input-group-append>
                </b-input-group>
              </o-form-group>

              <small class="form-text text-muted text-set" :class="{ 'd-none': item.status === 'Draft' }" v-if="item.actual_amount_of_educational && item.revUserName">{{ $t('sandoz.updated_by') }}: {{ item.revUserName }}</small>

              <b-form-checkbox v-model="item.plan_deviation_skip_approval" value="true" v-if="checkPlanDeviationSkipApproval()" :disabled="readonly">{{$t('sandoz.plan_deviation_skip_approval_info')}}</b-form-checkbox>
              <o-form-group v-if="item.type" :label="$t('sandoz.circulation')" class="mt-3">
                <table class="table table-sm">
                  <template v-for="i in [1,2,3,4,5,6]">
                    <tr v-if="item['approver_' + i]" :key="i">
                      <td>{{ item['approver_' + i] }}</td>
                      <td>
                        <span v-if="item['approval_person_' + i]" :title="item['approval_date_' + i]">
                          <fa icon="check"/> {{ item['approval_person_' + i] }}
                        </span>
                        <span v-else>
                          -
                        </span>
                      </td>
                    </tr>
                  </template>
                </table>
              </o-form-group>

              <o-textarea v-model="item.sales_rep_comment" :label="$t('sandoz.sales_rep_comment')" class="mt-3"></o-textarea>
              
              <template v-if="user.is_super_user">
                <o-textarea v-model="item.super_user_comment" :class="isCommentError && !item.super_user_comment ? 'has-error' : ''" :error-field="'super_user_comment'"
                            :label="$t('sandoz.super_user_comment')"
                            class="mt-3"></o-textarea>
              </template>
            </b-col>
          </b-row>

          <b-btn @click.prevent="activeTab++">{{$t('sandoz.next')}}</b-btn>
        </b-tab>
        <b-tab :title="$t('sandoz.items')">
          <b-overlay :show="item.type == null">
            <div class="card mb-2 d-none">
              <div class="d-flex card-body card-silver p-2">
                <table>
                  <tbody>
                  <tr>
                    <td>{{ $t('sandoz.marketing_activity_total_selected') }}</td>
                    <td class="text-right">{{ item.marketing_activities | formatNumber }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <template v-for="(address, index) in item.addresses">
              <template v-if="address.address_type === 'master'">
                <div class="d-flex">
                  <div class="pt-2">
                    <b>
                      {{ address.company_name ? address.company_name + ', ' : '' }}
                      {{ address.street ? address.street + ', ' : '' }}
                      {{ address.zip }} {{ address.city }}
                    </b>
                    <b-badge v-if="address.cap_exceeded" class="ml-2 d-none" href="#" pill variant="danger">
                      {{ $t('sandoz.cap_exceeded') }}
                    </b-badge>
                  </div>
                </div>
                <div class="table-responsive mt-3 ml-2 ice-item-table-div">
                  <table class="table table-sm ice-item-table">
                    <thead>
                    <tr>
                      <th class="" scope="col"></th>
                      <th class="mw-400" scope="col">{{ $t('sandoz.item') }}</th>
                      <th class="d-none" scope="col">{{ $t('sandoz.item_price') }}</th>
                      <th class="d-none" scope="col">{{ $t('sandoz.price') }}</th>
                      <th scope="col">{{ $t('sandoz.quantity') }}</th>
                      <th class="d-none" scope="col">{{ $t('sandoz.from') }}</th>
                      <th class="d-none" scope="col">{{ $t('sandoz.to') }}</th>
                    </tr>
                    </thead>
                    <tbody v-if="address.items && Object.keys(address.items).length">
                    <template v-for="(line, lineIndex) in address.items">
                      <tr class="master-item" :class="{'d-none': (line.product && line.product.product_master  && line.product.product_master.hold_actual_amount_of_educational === true)}">
                        <td>
                          <div v-if="checkLineChildren(line)"
                               class="item-accordion cursor-pointer">
                            <fa v-if="getAccordion(index, lineIndex) === -1" icon="plus"
                                @click="toggleAccordion(index, lineIndex)"/>
                            <fa v-if="getAccordion(index, lineIndex) !== -1" icon="minus"
                                @click="toggleAccordion(index, lineIndex)"/>
                          </div>
                        </td>
                        <td>
                          <div v-if="!line.allow_product_name_to_change">
                            {{ line.product ? line.product.name : line.product_name }}
                            <router-link
                              v-if="user && user.is_super_user && line.product && line.product.product_master_id"
                              :to="{ path: '/sandoz/product_masters/' + line.product.product_master_id }"
                              target="_blank">
                              <fa icon="pencil-alt"/>
                            </router-link>
                            <a v-if="line.product && line.product.info_link" :href="linkValidate(line.product.info_link)"
                               target="_blank">
                              <fa icon="file"/>
                            </a>
                            <fa v-if="line.product && line.product.product_master && line.product.product_master.picture"
                                icon="camera" class="text-primary cursor-pointer" @click="openPictureOverlay(line)"/>
                          </div>
                          <div v-else>
                            <o-input-control v-model="line.product_name" class="full-width"
                                             type="text"></o-input-control>
                            <router-link
                              v-if="user && user.is_super_user && line.product && line.product.product_master_id"
                              :to="{ path: '/sandoz/product_masters/' + line.product.product_master_id }"
                              target="_blank">
                              <fa icon="pencil-alt"/>
                            </router-link>
                            <a v-if="line.product && line.product.info_link" :href="linkValidate(line.product.info_link)"
                               target="_blank">
                              <fa icon="file"/>
                            </a>
                            <fa v-if="line.product && line.product.product_master && line.product.product_master.picture"
                                icon="camera" class="text-primary cursor-pointer" @click="openPictureOverlay(line)"/>
                          </div>
                        </td>
                        <td class="item-price d-none">{{ line.item_price | formatPrice }}</td>
                        <td class="d-none">
                          <template
                            v-if="line.price_changes && line.price_changes.price_changes === 'no_changes_allowed'">
                            <div class="w-100">
                              <div class="form-control text-right" readonly="readonly">{{ line.price | formatPrice }}
                              </div>
                            </div>
                          </template>
                          <template v-else>
                            <o-input-control v-model="line.price" :ignore-range="true" :step="1" class="form-price"
                                             type="number" @blur="validatePrice(line, index)"></o-input-control>
                          </template>
                        </td>
                        <td>
                          <template v-if="isReadonly(line, 'yearly') || (line.product && line.product.product_master && line.product.product_master.disabled)">
                            <div v-if="line.delivery.yearly" class="w-100 form-qty">
                              <div class="form-control" readonly="readonly">{{ line.delivery.yearly.quantity }}</div>
                            </div>
                          </template>
                          <template v-else>
                            <template
                              v-if="line.product_master_values && Object.keys(line.product_master_values).length">
                              <o-value-select v-model="line.delivery.yearly.quantity"
                                              :items="line.product_master_values"
                                              class="fixed-value-dropdown"
                                              text-field="value"
                                              value-field="value"
                                              @previous-value="setPreviousQuantity($event, line, 'yearly')"
                                              @change.native="setPrice(line, index, 'yearly'), setQuantityToChild(line, 'q1', index)">
                              </o-value-select>
                            </template>
                            <template v-else>
                              <o-input-control v-model="line.delivery.yearly.quantity" :ignore-range="true" :step="1"
                                               class="form-qty"
                                               type="number"
                                               @previous-value="setPreviousQuantity($event, line, 'yearly')"
                                               @blur="setPrice(line, index, 'yearly'), setQuantityToChild(line, 'q1', index)">
                              </o-input-control>
                            </template>
                          </template>
                        </td>
                        <td class="d-none">
                          <o-value-select-control v-model="line.valid_from_month" :items="months"
                                                  :readonly="!line.allow_manual_date_entry"
                                                  @change.native="setDateToChildren(line, index), setDateChanged(line, 'from')">
                          </o-value-select-control>
                        </td>
                        <td class="d-none">
                          <o-value-select-control v-model="line.valid_to_month" :items="months"
                                                  :readonly="!line.allow_manual_date_entry"
                                                  @change.native="setDateToChildren(line, index, 'to'), setDateChanged(line, 'to')">
                          </o-value-select-control>
                        </td>
                      </tr>
                      <template v-if="line.children && Object.keys(line.children).length">
                        <template v-for="(subLine, subLineIndex) in line.children">
                          <tr :class="{'d-none': (getAccordion(index, lineIndex) === -1)}">
                            <td>
                              <div class="item-accordion cursor-pointer"
                                  v-if="checkLineChildren(line)">
                                <fa icon="plus" @click="toggleAccordion(index, lineIndex)"
                                    v-if="getAccordion(index, lineIndex) === -1"/>
                                <fa icon="minus" @click="toggleAccordion(index, lineIndex)"
                                    v-if="getAccordion(index, lineIndex) !== -1"/>
                              </div>
                            </td>
                            <td v-if="!subLine.allow_product_name_to_change" class="indention">
                              {{ subLine.product ? subLine.product.name : subLine.product_name }}
                              <router-link
                                v-if="user && user.is_super_user && subLine.product && subLine.product.product_master_id"
                                :to="{ path: '/sandoz/product_masters/' + subLine.product.product_master_id }"
                                target="_blank">
                                <fa icon="pencil-alt"/>
                              </router-link>
                              <a v-if="subLine.product && subLine.product.info_link" :href="linkValidate(subLine.product.info_link)"
                                 target="_blank">
                                <fa icon="file"/>
                              </a>
                              <fa v-if="subLine.product && subLine.product.product_master && subLine.product.product_master.picture"
                                  icon="camera" class="text-primary cursor-pointer" @click="openPictureOverlay(subLine)"/>
                            </td>
                            <td v-else class="indention">
                              <o-input-control v-model="subLine.product_name" class="full-width"
                                               type="text"></o-input-control>
                              <router-link
                                v-if="user && user.is_super_user && subLine.product && subLine.product.product_master_id"
                                :to="{ path: '/sandoz/product_masters/' + subLine.product.product_master_id }"
                                target="_blank">
                                <fa icon="pencil-alt"/>
                              </router-link>
                              <a v-if="subLine.product && subLine.product.info_link" :href="linkValidate(subLine.product.info_link)"
                                 target="_blank">
                                <fa icon="file"/>
                              </a>
                              <fa v-if="subLine.product && subLine.product.product_master && subLine.product.product_master.picture"
                                  icon="camera" class="text-primary cursor-pointer" @click="openPictureOverlay(subLine)"/>
                            </td>
                            <td class="item-price d-none">{{ subLine.item_price | formatPrice }}</td>
                            <td class="d-none">
                              <template
                                v-if="subLine.price_changes && subLine.price_changes.price_changes === 'no_changes_allowed'">
                                <div class="w-100">
                                  <div class="form-control text-right" readonly="readonly">
                                    {{ subLine.price | formatPrice }}
                                  </div>
                                </div>
                              </template>
                              <template v-else>
                                <o-input-control v-model="subLine.price" :min="1" class="form-price"
                                                 type="number"
                                                 @blur="validatePrice(subLine, index)"></o-input-control>
                              </template>
                            </td>
                            <td>
                              <template v-if="isReadonly(subLine, 'yearly')">
                                <div v-if="subLine.delivery.yearly" class="w-100 form-qty">
                                  <div class="form-control" readonly="readonly">{{ subLine.delivery.yearly.quantity }}
                                  </div>
                                </div>
                              </template>
                              <template v-else>
                                <template
                                  v-if="subLine.product_master_values && Object.keys(subLine.product_master_values).length">
                                  <o-value-select v-model="subLine.delivery.yearly.quantity"
                                                  :items="subLine.product_master_values"
                                                  class="fixed-value-dropdown"
                                                  text-field="value"
                                                  value-field="value"
                                                  @previous-value="setPreviousQuantity($event, subLine, 'yearly')"
                                                  @change.native="setQuantityToParent(line, subLine, 'yearly', index),
                                                  setQuantityToChild(subLine, 'yearly', subLineIndex),
                                                  setBoxQuantity(line, 'yearly', subLine.box_or_gadget, index)">
                                  </o-value-select>
                                </template>
                                <template v-else>
                                  <o-input-control v-model="subLine.delivery.yearly.quantity" :ignore-range="true" :step="1"
                                                   class="form-qty"
                                                   type="number"
                                                   @previous-value="setPreviousQuantity($event, subLine, 'yearly')"
                                                   @blur="setQuantityToParent(line, subLine, 'yearly', index),
                                                  setQuantityToChild(subLine, 'yearly', subLineIndex),
                                                  setBoxQuantity(line, 'yearly', subLine.box_or_gadget, index)">
                                  </o-input-control>
                                </template>
                              </template>
                            </td>
                            <td class="d-none">
                              <o-value-select-control v-model="subLine.valid_from_month" :items="months"
                                                      :readonly="!subLine.allow_manual_date_entry"
                                                      @change.native="setDateToChildren(subLine, index, 'from'), setDateChanged(subLine, 'from')">
                              </o-value-select-control>
                            </td>
                            <td class="d-none">
                              <o-value-select-control v-model="subLine.valid_to_month" :items="months"
                                                      :readonly="!subLine.allow_manual_date_entry"
                                                      @change.native="setDateToChildren(subLine, index, 'to'), setDateChanged(subLine, 'to')">
                              </o-value-select-control>
                            </td>
                            <td class="text-right text-nowrap">
                            </td>
                          </tr>
                          <template v-if="subLine.children && Object.keys(subLine.children).length">
                            <template v-for="(childLine, childLineIndex) in subLine.children">
                              <tr :class="{'d-none': (getAccordion(lineIndex, subLineIndex) === -1 ||  getAccordion(index, lineIndex) === -1)}">
                                <td></td>
                                <td v-if="!childLine.allow_product_name_to_change" class="indention-1">
                                  {{ childLine.product ? childLine.product.name : childLine.product_name }}
                                  <router-link
                                    v-if="user && user.is_super_user && childLine.product && childLine.product.product_master_id"
                                    :to="{ path: '/sandoz/product_masters/' + childLine.product.product_master_id }"
                                    target="_blank">
                                    <fa icon="pencil-alt"/>
                                  </router-link>
                                  <a v-if="childLine.product && childLine.product.info_link" :href="linkValidate(childLine.product.info_link)"
                                     target="_blank">
                                    <fa icon="file"/>
                                  </a>
                                  <fa v-if="childLine.product && childLine.product.product_master && childLine.product.product_master.picture"
                                      icon="camera" class="text-primary cursor-pointer" @click="openPictureOverlay(childLine)"/>
                                </td>
                                <td v-else class="indention-1">
                                  <o-input-control v-model="childLine.product_name" class="full-width"
                                                   type="text"></o-input-control>
                                  <router-link
                                    v-if="user && user.is_super_user && childLine.product && childLine.product.product_master_id"
                                    :to="{ path: '/sandoz/product_masters/' + childLine.product.product_master_id }"
                                    target="_blank">
                                    <fa icon="pencil-alt"/>
                                  </router-link>
                                  <a v-if="childLine.product && childLine.product.info_link" :href="linkValidate(childLine.product.info_link)"
                                     target="_blank">
                                    <fa icon="file"/>
                                  </a>
                                  <fa v-if="childLine.product && childLine.product.product_master && childLine.product.product_master.picture"
                                      icon="camera" class="text-primary cursor-pointer" @click="openPictureOverlay(childLine)"/>
                                </td>
                                <td class="item-price d-none">{{ childLine.item_price | formatPrice }}</td>
                                <td class="d-none">
                                  <template
                                    v-if="childLine.price_changes && childLine.price_changes.price_changes === 'no_changes_allowed'">
                                    <div class="w-100">
                                      <div class="form-control text-right" readonly="readonly">
                                        {{ childLine.price | formatPrice }}
                                      </div>
                                    </div>
                                  </template>
                                  <template v-else>
                                    <o-input-control v-model="childLine.price" :min="1" class="form-price"
                                                     type="number"
                                                     @blur="validatePrice(childLine, index)"></o-input-control>
                                  </template>
                                </td>
                                <td>
                                  <template v-if="isReadonly(childLine, 'yearly')">
                                    <div v-if="childLine.delivery.yearly" class="w-100 form-qty">
                                      <div class="form-control" readonly="readonly">
                                        {{ childLine.delivery.yearly.quantity }}
                                      </div>
                                    </div>
                                  </template>
                                  <template v-else-if="childLine.delivery.yearly">
                                    <template
                                      v-if="childLine.product_master_values && Object.keys(childLine.product_master_values).length">
                                      <o-value-select v-model="childLine.delivery.yearly.quantity"
                                                      :items="childLine.product_master_values"
                                                      class="fixed-value-dropdown"
                                                      text-field="value"
                                                      value-field="value"
                                                      @previous-value="setPreviousQuantity($event, childLine, 'yearly')"
                                                      @change.native="setQuantityToParent(subLine, childLine, 'yearly', index),
                                                      setBoxQuantity(line, 'yearly', childLine.box_or_gadget, index)">
                                      </o-value-select>
                                    </template>
                                    <template v-else>
                                      <o-input-control v-model="childLine.delivery.yearly.quantity" :ignore-range="true" :step="1"
                                                       class="form-qty"
                                                       type="number"
                                                       @previous-value="setPreviousQuantity($event, childLine, 'yearly')"
                                                       @blur="setQuantityToParent(subLine, childLine, 'yearly', index),
                                                      setBoxQuantity(line, 'yearly', childLine.box_or_gadget, index)">
                                      </o-input-control>
                                    </template>
                                  </template>
                                </td>
                                <td class="d-none">
                                  <o-value-select-control v-model="childLine.valid_from_month" :items="months"
                                                          :readonly="!childLine.allow_manual_date_entry"
                                                          @change.native="setPrice(childLine, index), setDateChanged(childLine, 'from')">
                                  </o-value-select-control>
                                </td>
                                <td class="d-none">
                                  <o-value-select-control v-model="childLine.valid_to_month" :items="months"
                                                          :readonly="!childLine.allow_manual_date_entry"
                                                          @change.native="setPrice(childLine, index), setDateChanged(childLine, 'to')">
                                  </o-value-select-control>
                                </td>
                              </tr>
                            </template>
                          </template>
                        </template>
                      </template>
                    </template>
                    </tbody>
                    <tbody v-else>
                    <tr>
                      <td align="center" colspan="11">{{ $t('core.no_records') }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </template>
            </template>
            <template #overlay>
              <div class="text-center">
                <p>{{ $t('sandoz.set_type_first') }}</p>
              </div>
            </template>
          </b-overlay>

          <b-btn @click.prevent="activeTab--">{{$t('sandoz.previous')}}</b-btn>
          <b-btn @click.prevent="activeTab++">{{$t('sandoz.next')}}</b-btn>
        </b-tab>
        <b-tab :title="$t('sandoz.contract')">
          <o-button native-type="button" @click="confirmWithUser" class="btn btn-danger" target="_blank" :loading="pdfLoading" :disabled="pdfLoading">Download PDF</o-button>
          <a :href="vendorFormLinks[item.language]" class="btn btn-primary ml-2" target="_blank">{{ $t('sandoz.vendor_form') }}</a>
          <template v-if="draftInvoiceAvailable">
            <template v-if="item.payment_schema === 'semester'">
              <a :href="draftInvoiceS1Url" class="btn btn-danger ml-2" target="_blank">
                {{ $t('sandoz.download_draft_invoice_s1') }}
              </a>
              <a :href="draftInvoiceS2Url" class="btn btn-danger ml-2" target="_blank">
                {{ $t('sandoz.download_draft_invoice_s2') }}
              </a>
            </template>
            <a :href="draftInvoiceYearUrl" class="btn btn-danger ml-2" target="_blank">
              {{ $t('sandoz.download_draft_invoice') }}
            </a>
          </template>
          <template-download-button v-if="$route.params.id !== 'new'" :contract-id="$route.params.id" class="ml-2"/>

          <b-row class="mt-4">
            <b-col xl="8" lg="12" md="12" >

              <o-value-select :items="paymentSchemas" :label="$t('sandoz.payment_schema')" v-model="item.payment_schema" :readonly="readonly"/>

              <template v-for="i in [1,2,3,4,5,6,7]">
                <o-form-group :key="i" :label="$t('sandoz.contract_partner_' + i)">
                  <b-container class="w-100 px-0">
                    <b-row>
                      <b-col class="pr-1">
                        <b-input-group :prepend="$t('sandoz.name')">
                          <b-form-input v-model="item['contract_partner_' + i]" :disabled="readonly"></b-form-input>
                        </b-input-group>
                      </b-col>
                      <b-col class="pl-1">
                        <b-input-group :prepend="$t('sandoz.title')">
                          <b-form-input v-model="item['contract_partner_' + i + '_title']" :disabled="readonly"></b-form-input>
                        </b-input-group>
                      </b-col>
                      <b-col class="pl-1">
                        <select class="custom-select" v-model="item['contract_partner_' + i + '_title_dropdown']" @change="setTitle(i)" :disabled="readonly">
                          <option v-for="titleItem in titleItems" :value="titleItem.value" :key="titleItem.value">{{titleItem.text}}</option>
                        </select>
                      </b-col>
                    </b-row>
                  </b-container>
                </o-form-group>
              </template>
            </b-col>
            <b-col xl="4"  lg="12" md="12">
              <o-form-group>
                <b-form-checkbox v-model.lazy="item.vat" name="vat" value="x" :disabled="readonly">
                  {{ $t('sandoz.vat_obligation') }}
                </b-form-checkbox>
              </o-form-group>

              <o-input v-if="item.vat == 'x'" v-model="item.vat_number" :errorField="'vat_number'"
                       :label="$t('sandoz.vat_number')" :readonly="readonly"/>
              <o-input v-model="item.bank_name" :errorField="'bank_name'" :label="$t('sandoz.bank_name')" :readonly="readonly"/>
              <o-input v-model="item.bank_address" :errorField="'bank_address'" :label="$t('sandoz.bank_address')" :readonly="readonly"/>
              <o-input v-model="item.accountholder_name" :errorField="'accountholder_name'"
                       :label="$t('sandoz.accountholder_name')" :readonly="readonly"/>
              <o-input v-model="item.accountholder_address" :errorField="'accountholder_address'"
                       :label="$t('sandoz.accountholder_address')" :readonly="readonly"/>
              <o-input v-model="item.iban_no" :errorField="'iban_no'" :label="$t('sandoz.iban_no')" :readonly="readonly"/>
              <o-input v-model="item.accountholder_email" :errorField="'accountholder_email'"
                       :label="$t('sandoz.accountholder_email')" :readonly="readonly"/>
            </b-col>
          </b-row>
          <b-btn @click.prevent="activeTab--">{{$t('sandoz.previous')}}</b-btn>
          <b-btn @click.prevent="activeTab++">{{$t('sandoz.next')}}</b-btn>
        </b-tab>

        <b-tab :title="$t('sandoz.files')">
          <contract-file :item="item" :user="user"></contract-file>
          <b-btn @click.prevent="activeTab--">{{$t('sandoz.previous')}}</b-btn>
        </b-tab>
      </b-tabs>
      <silent-box :gallery="pictures" :lazy-loading="true" class="silentbox-overlay-only" ref="imageSilentBox">
      </silent-box>
      <address-search ref="addressSearch" :address-mode-checkbox="showAddressModeCheckbox" contract-type="educational"
                      @address-selected="addressSelected"></address-search>
    </o-card>
    <o-action-toolbar>
      <div class="d-flex w-100">
        <o-button type="button" variant="outline-primary" @click.prevent="back">{{ $t('core.back') }}</o-button>
        <div class="ml-auto">
          <o-button
            v-if="(item && item.status == 'Signed')"
            :loading="saving" type="button" variant="primary"
            @click.prevent="startVerification()">{{ customerCount === 0 ? $t('sandoz.start_verification_process')  : $t('sandoz.resend_verification_process') }}
          </o-button>
          <o-button
            v-if="(user && user.is_super_user && item && (item.status == 'Committed' || item.status == 'Approved' || item.status == 'Signed'))"
            :loading="saving" type="button" variant="primary"
            @click.prevent="$refs.resetContractModel.show()">{{ $t('sandoz.reset_to_draft') }}
          </o-button>
          <o-button v-if="(item && (item.status == 'Draft' || item.status == 'Feedback'))" :loading="saving"
                    type="button" variant="primary"
                    @click.prevent="contractCanApprovable">{{ $t('sandoz.upload_start_approval') }}
          </o-button>
          <o-button v-if="(item && (item.status == 'Draft'))" :loading="saving" type="button" variant="primary"
                    @click.prevent="save">{{ $t('sandoz.save_draft') }}
          </o-button>
          <o-button v-if="(item && (item.status == 'Feedback'))" :loading="saving" type="button" variant="primary"
                    @click.prevent="save">{{ $t('core.save') }}
          </o-button>
        </div>
      </div>
    </o-action-toolbar>
    <b-modal id="modal-previous-contract" ref="addPreviousContractModel" :ok-only="true"
             :ok-title="$t('core.cancel')" :title="$t('sandoz.select_previous_contract')" size="xl">
      <o-table ref="previousContractList" :enable-delete="false" :enable-edit="false"
               :enable-new="false"
               :only-filterable="true" :shadow="false" title=""
               url="/api/sandoz/contracts?include=master_address_single">
        <template slot="filter">
          <o-filter v-on:filter-updated="(filters) => $refs.previousContractList.setFilters(filters)">
            <o-filter-text :title="$t('sandoz.contract_no')" column="contract_no"></o-filter-text>
            <o-filter-text :title="$t('sandoz.customer_external_id')" column="customer_external_id"></o-filter-text>
            <o-filter-select :items="years" :title="$t('sandoz.contract_year')"
                             column="contract_year"></o-filter-select>
          </o-filter>
        </template>
        <template slot="head">
          <o-table-header column="contract_no">{{ $t('sandoz.contract_no') }}</o-table-header>
          <o-table-header column="customer_external_id">{{ $t('sandoz.customer_external_id') }}</o-table-header>
          <o-table-header column="contract_year">{{ $t('sandoz.contract_year') }}</o-table-header>
          <o-table-header :sortable="false">{{ $t('sandoz.name') }}</o-table-header>
          <o-table-header :sortable="false">{{ $t('sandoz.last_name') }}</o-table-header>
          <o-table-header :sortable="false">{{ $t('sandoz.company_name') }}</o-table-header>
          <o-table-header :sortable="false">{{ $t('sandoz.street') }}</o-table-header>
          <o-table-header :sortable="false">{{ $t('sandoz.zip') }}</o-table-header>
          <o-table-header :sortable="false">{{ $t('sandoz.city') }}</o-table-header>
        </template>
        <template slot="body" slot-scope="previousContractProps">
          <td>{{ previousContractProps.item.contract_no }}</td>
          <td>{{ previousContractProps.item.customer_external_id }}</td>
          <td>{{ previousContractProps.item.contract_year }}</td>
          <td>{{
              previousContractProps.item.master_address_single && previousContractProps.item.master_address_single.data ?
                previousContractProps.item.master_address_single.data.name : ''
            }}
          </td>
          <td>{{
              previousContractProps.item.master_address_single && previousContractProps.item.master_address_single.data ?
                previousContractProps.item.master_address_single.data.last_name : ''
            }}
          </td>
          <td>{{
              previousContractProps.item.master_address_single && previousContractProps.item.master_address_single.data ?
                previousContractProps.item.master_address_single.data.company_name : ''
            }}
          </td>
          <td>{{
              previousContractProps.item.master_address_single && previousContractProps.item.master_address_single.data ?
                previousContractProps.item.master_address_single.data.street : ''
            }}
          </td>
          <td>{{
              previousContractProps.item.master_address_single && previousContractProps.item.master_address_single.data ?
                previousContractProps.item.master_address_single.data.zip : ''
            }}
          </td>
          <td>{{
              previousContractProps.item.master_address_single && previousContractProps.item.master_address_single.data ?
                previousContractProps.item.master_address_single.data.city : ''
            }}
          </td>
        </template>
        <template slot="body-controls" slot-scope="previousContractProps">
          <td class="text-right">
            <div v-if="previousContractProps.item.id === item.id">
              <span class="badge badge-success">Current </span>
            </div>
            <div v-else-if="previousContractProps.item.id === item.previous_contract">
              <span class="badge badge-warning">Previous</span>
            </div>
            <div v-else aria-label="edit controls" class="btn-group btn-group-sm" role="group">
              <b-btn variant="primary" @click="addPreviousContract(previousContractProps.item)">
                <fa icon="arrow-right"/>
              </b-btn>
            </div>
          </td>
        </template>
      </o-table>
    </b-modal>
    <b-modal id="modal-reset-contract" ref="resetContractModel" :title="$t('sandoz.reset_to_draft')">
      {{ $t('sandoz.reset_confirmation') }}
      <template #modal-footer="{ ok, cancel, hide }">
        <b-button variant="secondary" @click="cancel()">
          {{ $t('core.cancel') }}
        </b-button>
        <b-button variant="primary" @click="resetToDraft()">
          {{ $t('core.ok') }}
        </b-button>
      </template>
    </b-modal>
    <b-modal id="modal-duplicate-contract" ref="duplicateContractModel" :title="$t('sandoz.duplicate_contract')">
      {{ $t('sandoz.duplicate_contract_error') }}
      <template #modal-footer="{ ok, cancel, hide }">
        <b-button variant="secondary" @click="showDuplicateContract">
          {{ $t('sandoz.show_contract') }}
        </b-button>
        <b-button variant="primary" @click="cancel()">
          {{ $t('core.ok') }}
        </b-button>
      </template>
    </b-modal>
  </form>
</template>

<script>
import {mapGetters} from 'vuex'
import axios from 'axios'
import {contractYears} from '../../../utils/contractYear'
import AddressSearch from '../partials/AddressSearch.vue'
import OValueSelectControl from '../../../components/OrticValueSelectControl.vue'
import moment from 'moment'
import {diff} from '~/functions/object.js'
import ContractFile from '../partials/ContractFiles.vue'
import TemplateDownloadButton from '../partials/TemplateDownloadButton.vue'

export default {
  components: {
    OValueSelectControl,
    AddressSearch,
    ContractFile,
    TemplateDownloadButton
  },
  data: () => ({
    contractTypes: [],
    years: [],
    item: null,
    watchers: [],
    saving: false,
    toggleItems: false,
    listItem: [],
    selectedItems: [],
    searchItem: null,
    months: [],
    loaded: false,
    currentUser: null,
    watcherWorking: false,
    showAddressModeCheckbox: false,
    changeExternalID: false,
    downloadPDF: false,
    itemOld: null,
    languages: [],
    setLang: false,
    accordionItems: [],
    showContract: null,
    determErrorID: [],
    isCommentError: false,
    pictures: [],
    pdfLoading: false,
    activeTab: 0,
    vendorFormLinks: {
      DE: 'https://sandozcollab.sharepoint.com/:w:/r/sites/sandoz-schweiz-intranet/teamsites/Freigegebene%20Dokumente/Marketing/ZZ_NICHT%20LOESCHEN%20ODER%20VERSCHIEBEN/Formular%20f%C3%BCr%20Vendorer%C3%B6ffnung_DE_2024.doc?d=w429524bc2ddd45dcaa24f693c1aaae5b&csf=1&web=1&e=508gVl&xsdata=MDV8MDJ8YmVhdC5zdGVpbmVnZ2VyQHNhbmRvei5jb218NzA0NDBhMWNlMjJkNGRjMmQ5ZDcwOGRlMTFmZjRhN2V8MTVlYWUyY2IxYzk0NGQxZjgwNGZjYjBjMzQ3ZGJkYWR8MHwwfDYzODk2Nzk4NTQ4NTg1NDkxMXxVbmtub3dufFRXRnBiR1pzYjNkOGV5SkZiWEIwZVUxaGNHa2lPblJ5ZFdVc0lsWWlPaUl3TGpBdU1EQXdNQ0lzSWxBaU9pSlhhVzR6TWlJc0lrRk9Jam9pVFdGcGJDSXNJbGRVSWpveWZRPT18MHx8fA%3d%3d&sdata=ZWloUzZNUGk4NWgrRzRvKzFsQmhIeWI3T01rTGpTU1V3M3pEYWI5dGRNQT0%3d',
      FR: 'https://sandozcollab.sharepoint.com/:w:/r/sites/sandoz-schweiz-intranet/teamsites/Freigegebene%20Dokumente/Marketing/ZZ_NICHT%20LOESCHEN%20ODER%20VERSCHIEBEN/Formular%20f%C3%BCr%20Vendorer%C3%B6ffnung_FR_2024.doc?d=w49f246b7c89848be897188826334fc2b&csf=1&web=1&e=pID1ir&xsdata=MDV8MDJ8YmVhdC5zdGVpbmVnZ2VyQHNhbmRvei5jb218NzA0NDBhMWNlMjJkNGRjMmQ5ZDcwOGRlMTFmZjRhN2V8MTVlYWUyY2IxYzk0NGQxZjgwNGZjYjBjMzQ3ZGJkYWR8MHwwfDYzODk2Nzk4NTQ4NTgwMDY5MHxVbmtub3dufFRXRnBiR1pzYjNkOGV5SkZiWEIwZVUxaGNHa2lPblJ5ZFdVc0lsWWlPaUl3TGpBdU1EQXdNQ0lzSWxBaU9pSlhhVzR6TWlJc0lrRk9Jam9pVFdGcGJDSXNJbGRVSWpveWZRPT18MHx8fA%3d%3d&sdata=TFNEOTgyN25YM3ppR2xlVXRxRGpFR0dna3F2YURkV0JRYW9uaktaZFNPND0%3d',
      IT: 'https://sandozcollab.sharepoint.com/:w:/r/sites/sandoz-schweiz-intranet/teamsites/Freigegebene%20Dokumente/Marketing/ZZ_NICHT%20LOESCHEN%20ODER%20VERSCHIEBEN/Formular%20f%C3%BCr%20Vendorer%C3%B6ffnung_IT_2024.doc?d=w6a072b20b8504e1c9ff128710757020e&csf=1&web=1&e=umGkd2&xsdata=MDV8MDJ8YmVhdC5zdGVpbmVnZ2VyQHNhbmRvei5jb218NzA0NDBhMWNlMjJkNGRjMmQ5ZDcwOGRlMTFmZjRhN2V8MTVlYWUyY2IxYzk0NGQxZjgwNGZjYjBjMzQ3ZGJkYWR8MHwwfDYzODk2Nzk4NTQ4NTgzMTc0MnxVbmtub3dufFRXRnBiR1pzYjNkOGV5SkZiWEIwZVUxaGNHa2lPblJ5ZFdVc0lsWWlPaUl3TGpBdU1EQXdNQ0lzSWxBaU9pSlhhVzR6TWlJc0lrRk9Jam9pVFdGcGJDSXNJbGRVSWpveWZRPT18MHx8fA%3d%3d&sdata=b3pRT1dTRXlXcjkwaE5Lbm1tNjFTU0g4SERKd2ZwUzh3bU9RcjhrOGUwWT0%3d'
    },
    paymentSchemas: [],
    titleItems: [],
    customerCount: 0
  }),
  mounted() {
    this.loadData()
  },
  computed: {
    ...mapGetters({
      user: 'auth/user'
    }),
    readonly() {
      return this.item && this.item.status != 'Draft' && this.item.status != 'Feedback'
    },
    draftInvoiceAvailable () {
      return this.item.id !== undefined && this.item.contract_year ? moment((this.item.contract_year - 1) + '-12-01').isSameOrBefore(moment().format('YYYY-MM-DD')) : false
    },
    draftInvoiceS1Available () {
      if (!this.item || !this.item.contract_start) {
        return true
      }
      const startMonth = moment(this.item.contract_start).month() + 1
      return startMonth <= 5
    },
    draftInvoiceS1Url () {
      if (!this.item || !this.item.id) {
        return '#'
      }
      return `/api/sandoz/contracts/${this.item.id}/invoice/1/6`
    },
    draftInvoiceS2Url () {
      if (!this.item || !this.item.id) {
        return '#'
      }
      return `/api/sandoz/contracts/${this.item.id}/invoice/7/12`
    },
    draftInvoiceYearUrl () {
      if (!this.item || !this.item.id) {
        return '#'
      }
      return `/api/sandoz/contracts/${this.item.id}/invoice/1/12`
    },
  },
  watch: {
    'item.contract_start': function (newVal) {
      this.setContractDateToItems()
    },
    saving: function (newVal, oldVal) {
      const self = this
      if (!newVal) {
        setTimeout(function (){
          self.pdfLoading = newVal
        }, 1000)
      } else {
        self.pdfLoading = newVal
      }
    }
  },
  created() {
    this.contractTypes = [
      {value: 'SD_EDU', text: this.$i18n.t('sandoz.sd_edu')},
      {value: 'CNS_EDU', text: this.$i18n.t('sandoz.cns_edu')},
      {value: 'IMMUNO_EDU', text: this.$i18n.t('sandoz.immuno_edu')},
      {value: 'ONCO_EDU', text: this.$i18n.t('sandoz.onco_edu')},
      {value: 'KAM_SD_EDU', text: this.$i18n.t('sandoz.kam_sd_edu')},
      {value: 'APO_EDU', text: this.$i18n.t('sandoz.apo_edu')},
      {value: 'KAM_APO_EDU', text: this.$i18n.t('sandoz.kam_apo_edu')}
    ]

    this.languages = [
      {value: 'DE', text: 'DE'},
      {value: 'FR', text: 'FR'},
      {value: 'IT', text: 'IT'}
    ]

    this.paymentSchemas = [
      {value: 'semester', text: this.$i18n.t('sandoz.semester')},
      {value: 'yearly', text: this.$i18n.t('sandoz.yearly')}
    ]
  },
  methods: {
    setYears(type = null) {
      if(type === 'KAM_SD_EDU') {
        this.years = contractYears().filter(value => value.value > 2023)
      } else {
        this.years = [{ value: 2024, text: '2024' }, { value: 2025, text: '2025' }, { value: 2026, text: '2026' }]
      }
    },
    loadData() {
      this.saving = true
      var url = `/api/sandoz/educational/contracts/${this.$route.params.id}?include=word_files.user,pdfs.user,invoices`
      this.url = url
      axios
        .get(url)
        .then((response) => {
          var item = response
          if (this.dataKey) {
            item = response[this.dataKey]
          }
          this.item = item.data

          // set default user
          if (!this.item.user_id) {
            this.item.user_id = this.user.id
          }
          this.setYears(this.item.type)
          this.setTitleItems()
          var partners = [1,2,3,4,5,6,7];
          for(var i=1; i<=partners.length; i++) {
            this.item['contract_partner_' + i + '_title_dropdown'] = null
          }
          this.getAddressItemData()
          //this.setContractNo()
          this.loadplanData()
          this.checkCustomerLinkWithExternalID()
        })
    },
    enableWatchers() {
      this.watchers.push(this.$watch('item.customer_external_id', (newVal) => {
        this.changeExternalID = true
        this.setContractNo()
        this.setPreviousContract()
      }))
      this.watchers.push(this.$watch('item.contract_year', (newVal) => {
        this.setContractNo()
        this.setPreviousContract()
        this.addItemsToAddresses(false)
        this.changeContractDuration()
        this.loadplanData()
        this.setApprovers()
      }))
      this.watchers.push(this.$watch('item.language', (newVal) => {
        this.addItemsToAddresses(false)
        this.setTitleItems()
        this.setLang = true
      }))
      this.watchers.push(this.$watch('item.type', (newVal) => {
        this.setYears(newVal)
        this.setContractNo()
        this.setSalesLine()
        this.loadplanData()
        this.setApprovers()
        
      }))
      this.watchers.push(this.$watch('item.no_of_medical_practices', (newVal) => {
        this.setAmountNeed()
      }))
      this.watchers.push(this.$watch('item.max_threshold_per_hcp', (newVal) => {
        this.setAmountNeed()
      }))
      this.watchers.push(this.$watch('item.total_training_budget', (newVal) => {
        this.setAmountNeed()
      }))
      this.watchers.push(this.$watch('item.planned_amount_of_educational', (newVal) => {
        this.setAmountNeed()
        this.setApprovers()
      }))

      this.watchers.push(this.$watch('item.contract_start', (newVal) => {
        if(this.item && (this.item.status == 'Draft' || this.item.status == 'Feedback')) {
          this.loadplanData()
        }
      }))

      this.watchers.push(this.$watch('item.contract_expiration', (newVal) => {
        if(this.item && (this.item.status == 'Draft' || this.item.status == 'Feedback')) {
          this.loadplanData()
        }
      }))

      this.watchers.push(this.$watch('item.plan_deviation_skip_approval', (newVal) => {
        this.setApprovers()
      }))
    },
    disableWatchers() {

    },
    setAmountNeed () {
      if(!this.item.no_of_medical_practices || !this.item.max_threshold_per_hcp) {
        this.item.amount_of_educational = null
        this.item.actual_amount_of_educational = null
        return
      }

      // calculate 60% of training budget
      var maxBudget = null
      if(this.item.total_training_budget) {
        maxBudget = (this.item.total_training_budget * 60)/ 100
      }

      var maxAmountPerDoctor = this.item.max_threshold_per_hcp && this.item.max_threshold_per_hcp > 3000 ? 3000 : this.item.max_threshold_per_hcp
      var amount_of_educational = this.item.no_of_medical_practices * maxAmountPerDoctor

      if(maxBudget !== null) {
        var amount_of_educational = (amount_of_educational < maxBudget) ? amount_of_educational : maxBudget
      }

      this.item.amount_of_educational = Number(amount_of_educational).toFixed(2)
      if(!this.item.actual_amount_of_educational || this.item.status === 'Draft') {
        if(this.item.planned_amount_of_educational) {
          this.item.actual_amount_of_educational = Number(this.item.planned_amount_of_educational).toFixed(2)
        } else {
          this.item.actual_amount_of_educational = null
        }
      }

      this.$forceUpdate()
    },
    contractCanApprovable() {
      this.saveAndStartApproval()
    },
    saveAndStartApproval() {

      if(this.item.planned_amount_of_educational === '' || this.item.planned_amount_of_educational === null || parseInt(this.item.planned_amount_of_educational) === 0) {
        this.$notify({
          title: this.$i18n.t('core.validation_error'),
          text: this.$i18n.t('core.validation_error_text', { message: this.$i18n.t('sandoz.planned_amount_not_blank') }),
          type: 'error'
        })
        return
      }

      // check bank information mandatory
      if (this.item.sales_line && this.item.sales_line.bank_information_mandatory === true) {
        const validations = [
          { field: 'bank_name', message: this.$i18n.t('sandoz.bank_name_required') },
          { field: 'bank_address', message: this.$i18n.t('sandoz.bank_address_required') },
          { field: 'accountholder_name', message: this.$i18n.t('sandoz.account_holder_name_required') },
          { field: 'accountholder_address', message: this.$i18n.t('sandoz.account_holder_address_required') },
          { field: 'iban_no', message: this.$i18n.t('sandoz.iban_no_required') },
          { field: 'accountholder_email', message: this.$i18n.t('sandoz.account_holder_email_required') },
        ]

        for (let v of validations) {
          const value = this.item[v.field]

          if (!value || value.trim() === '' || value.trim() === null) {
            this.$notify({
              title: this.$i18n.t('core.validation_error'),
              text: this.$i18n.t('core.validation_error_text', { message: v.message }),
              type: 'error'
            })
            return
          }
        }
      }

      this.item.status = 'Committed'
      this.save()
    },
    resetToDraft() {
      this.item.status = 'Draft'

      this.item.approval_date_1 = null
      this.item.approval_person_1 = null

      this.item.approval_date_2 = null
      this.item.approval_person_2 = null

      this.item.approval_date_3 = null
      this.item.approval_person_3 = null

      this.item.approval_date_4 = null
      this.item.approval_person_4 = null

      this.item.approval_date_5 = null
      this.item.approval_person_5 = null

      this.item.approval_date_6 = null
      this.item.approval_person_6 = null

      this.item.next_approver = null
      this.item.contract_version = (!this.item.contract_version || this.item.contract_version == 1) ? 2 : this.item.contract_version + 1
      this.save(true)
      this.$refs.resetContractModel.hide()
    },
    checkMinDoctor () {
      return this.item.no_of_medical_practices && this.item.no_of_medical_practices < 3
    },
    checkAmount () {
      return (((this.item.amount_of_educational || this.item.actual_amount_of_educational) && parseInt(this.item.actual_amount_of_educational) > parseInt(this.item.amount_of_educational)) || 
      ((this.item.amount_of_educational || this.item.planned_amount_of_educational) && parseInt(this.item.actual_amount_of_educational) > parseInt(this.item.planned_amount_of_educational))
      )
    },
    checkBudget () {
      const maxBudget = (this.item.total_training_budget * 60)/ 100
      return !!(this.item.total_training_budget && parseInt(this.item.amount_of_educational) > parseInt(maxBudget))
    },
    checkMaxThreshold () {
      return this.item.max_threshold_per_hcp && parseInt(this.item.max_threshold_per_hcp) > 3000
    },
    save(skipValidation = false) {
      var url = this.url
      var route = this.$route

      // this happens if we create a new data record and update it right away without navigating way. We'll still have
      // new in our url, but we don't want to create a new record, we want to update the record we just created.
      // @TODO we should do this in a more elegant way that doesn't require search & replace
      if (url.indexOf('/new') > 0 && 'id' in this.item) {
        url = url.replace('/new', '/' + this.item.id)
      }

      var root = this.$root
      var self = this

      if(!skipValidation) {
        if(self.item.type ===  null) {
          self.$notify({
            title: self.$i18n.t('core.validation_error'),
            text: self.$i18n.t('core.validation_error_text', { message: self.$i18n.t('sandoz.type_required') }),
            type: 'error'
          })
          return
        }

        if(self.item.customer_external_id ===  null) {
          self.$notify({
            title: self.$i18n.t('core.validation_error'),
            text: self.$i18n.t('core.validation_error_text', { message: self.$i18n.t('sandoz.external_id_required') }),
            type: 'error'
          })
          return
        }

        if(self.item.contract_year ===  null) {
          self.$notify({
            title: self.$i18n.t('core.validation_error'),
            text: self.$i18n.t('core.validation_error_text', { message: self.$i18n.t('sandoz.contract_year_required') }),
            type: 'error'
          })
          return
        }

        if(self.checkMinDoctor() || !self.item.no_of_medical_practices) {
          self.$notify({
            title: self.$i18n.t('core.validation_error'),
            text: self.$i18n.t('core.validation_error_text', { message: self.$i18n.t(self.item.type === 'APO_EDU' ? 'sandoz.min_pharmacists_error' : 'sandoz.min_doctors_error') }),
            type: 'error'
          })
          return
        }

        if(self.checkPlannedAmount()) {
          self.$notify({
            title: self.$i18n.t('core.validation_error'),
            text: self.$i18n.t('core.validation_error_text', { message: self.$i18n.t('sandoz.planned_amount_must_smaller_actual_amount') }),
            type: 'error'
          })
          return
        }

        if(self.checkMaxThreshold()) {
          self.$notify({
            title: self.$i18n.t('core.validation_error'),
            text: self.$i18n.t('core.validation_error_text', { message: self.$i18n.t('sandoz.threshold_exceed') }),
            type: 'error'
          })
          return
        }

        if(self.checkAmount()) {
          self.$notify({
            title: self.$i18n.t('core.validation_error'),
            text: self.$i18n.t('core.validation_error_text', { message: self.$i18n.t('sandoz.actual_amount_exceed') }),
            type: 'error'
          })
          return
        }

        if(self.checkBudget()) {
          self.$notify({
            title: self.$i18n.t('core.validation_error'),
            text: self.$i18n.t('core.validation_error_text', { message: self.$i18n.t('sandoz.training_budget_exceed') }),
            type: 'error'
          })
          return
        }

        if(self.checkPlannedWithActualAmount()) {
          self.$notify({
            title: self.$i18n.t('core.validation_error'),
            text: self.$i18n.t('core.validation_error_text', { message: self.$i18n.t('sandoz.actual_education_amount_less_planned_educational') }),
            type: 'error'
          })
          return
        }

        if(self.quantityMissing()) {
          self.$notify({
            title: self.$i18n.t('core.validation_error'),
            text: self.$i18n.t('core.validation_error_text', { message: self.$i18n.t('sandoz.quantity_missing_error') }),
            type: 'error'
          })
          return
        }
      }

      this.disableWatchers()

      this.saving = true
      this.showContract = null

      axios
        .post(url, this.item)
        .then((response) => {
          let savedItem = response.data
          // this.item = response.data

          // assign our new id to the route params in case someone wants to work with it
          if ('id' in response.data) {
            this.item.id = response.data.id
            route.params.id = response.data.id
          }

          if ('updated_at' in response.data) {
            this.$set(this.item, 'updated_at', response.data.updated_at)
          }

          this.$notify({
            title: this.$i18n.t('core.saved'),
            text: this.$i18n.t('core.saved_text')
          })

          // remove all error states
          var elements = document.querySelectorAll('.has-error')
          elements.forEach((element) => {
            element.classList.remove('has-error')
          })

          // clear all errors
          root.errors = {}
          this.isCommentError = false

          this.showDeletedFiles = false
          this.showDeletedPDFFiles = false
          if (this.downloadPDF) {
            this.pdfContract()
            this.$nextTick(function (){
              this.itemOld = {...this.item}
              this.downloadPDF = false
              this.saving = false
            })
          } else {
            this.loaded = true
            this.itemOld = {...this.item}
            this.setWholesalers(savedItem)
            this.getAddressItemData()
            this.saving = false
          }
        })
        .catch((error) => {
          // stop loading indicator
          self.saving = false

          if (error.response.status === 409) {
            this.$bvModal.msgBoxConfirm(self.$i18n.t('core.concurrent_modification_text') || 'Data was changed by another process. Do you want to reload the page to see the new data?', {
                title: self.$i18n.t('core.concurrent_modification') || 'Data Changed',
                size: 'md',
                buttonSize: 'sm',
                okVariant: 'primary',
                okTitle: self.$i18n.t('core.reload') || 'Reload Page',
                cancelTitle: self.$i18n.t('core.cancel') || 'Cancel',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            }).then(value => {
                if(value) {
                    window.location.reload();
                }
            })
          } else if (error.response.status === 422 && 'data' in error.response && 'message' in error.response.data) {
            this.$notify({
              title: this.$i18n.t('core.validation_error'),
              text: this.$i18n.t('core.validation_error_text', {message: error.response.data.message}),
              type: 'error'
            })
            if ('data' in error.response && 'errors' in error.response.data) {
              root.errors = error.response.data.errors
            }
          } else {
            if (error.response.data && 'contract_error' in error.response.data && error.response.data.contract_error === 'contract_already_exists') {
              this.showContract = error.response.data.contract
              this.$refs.duplicateContractModel.show()
            } else {
              this.$notify({
                title: this.$i18n.t('core.save_error'),
                text: this.$i18n.t('core.save_error_text', {url: this.url}),
                type: 'error'
              })
            }
          }
        })
    },
    back() {
      this.$router.back()
    },
    setContractNo() {
      if (!this.item.type || !this.item.customer_external_id) {
        return
      }

      var contractNo = `${this.item.type}-${this.item.contract_year - 2000}-${this.item.customer_external_id}`
      if (this.item.addresses.length > 0) {
        let currentAdr = this.item.addresses[0]
        if (currentAdr.company_name) {
          contractNo += `-${currentAdr.company_name}`
        }

        if (currentAdr.city) {
          contractNo += `-${currentAdr.city}`
        }
      }
      this.item.contract_no = contractNo
    },
    setCurrentUser(user) {
      if (!user) {
        return
      }

      this.currentUser = user

      if (user.regions && user.regions.data && Object.keys(user.regions.data).length) {
        this.item.region_id = user.regions.data[0].region_id
      } else {
        this.item.region_id = null
      }
    },
    changeContractDuration() {
      const startDate = moment(this.item.contract_start)
      const startYear = startDate.format('YYYY')
      if (startYear !== this.item.contract_year) {
        this.item.contract_start = this.item.contract_year + '-01-01'
      }
    },
    setApprover(num, val) {
      if (this.item['approver_' + num] != val) {
        this.item['approver_' + num] = val

        // clear existing
        for (let i = num; i < 6; i++) {
          this.item['approval_date_' + i] = null
          this.item['approval_person_' + i] = null
        }

        // reset next approver if needed
        this.item.next_approver = Math.min(this.item.next_approver, num)
      }
    },
    setApprovers() {
      if (this.item.status != 'Draft' && this.item.status != 'Feedback') {
        return
      }
      if (!this.item.type) {
        return
      }

      for (let i = 1; i <= 6; i++) {
        this.setApprover(i, null)
      }

      var investZeroError = false
      if(this.item.planned_amount_of_educational <= 0 || this.item.amount_of_educational <= 0) {
        investZeroError = true
        this.item.deviation_outside_margin = null
      }

      // No deviation if plandata null
      if(this.planData === null) {
        this.item.deviation_outside_margin = null
      }

      if((this.item.sales_line && this.item.sales_line.automatic_approval_allowed === true) && this.planData !== null) {
        if((this.plan_invest_condition === true) &&
          (this.plan_invest_outside_range !== true)
        ) {
          this.item.deviation_outside_margin = 0
        }

        if((this.plan_invest_condition === true) &&
          (this.plan_invest_outside_range == true)
        ) {
          this.item.deviation_outside_margin = 1
        }
      }


      if((investZeroError === false && (this.planData === null || (this.item.sales_line && this.item.sales_line.automatic_approval_allowed === false)) ||
            ((this.item.sales_line && this.item.sales_line.automatic_approval_allowed === true) && this.item.plan_deviation_skip_approval === false && this.planData !== null &&
              ((this.plan_invest_outside_range === true) || (this.plan_invest_condition === true)))
          )) {

        if (this.item.type === 'KAM_SD_EDU') {
          this.item.contract_year >= 2025 ? this.setApprover(1, 'BU Head Retail') : this.setApprover(1, 'Channel Head')
        } else {
          this.item.contract_year >= 2025 ? this.setApprover(1, 'Channel Head') : this.setApprover(1, 'FLM')
        }
        this.item.contract_year >= 2025 ? this.setApprover(2, 'BPA') : this.setApprover(2, 'Medical')
        this.item.contract_year >= 2025 ? this.setApprover(3, 'Legal & Compliance') : this.setApprover(3, 'BPA')
        this.item.contract_year >= 2025 ? null : this.setApprover(4, 'Compliance')

        this.item.next_approver = 1
      } else {
        for (let i = 1; i <= 6; i++) {
          this.setApprover(i, null)
        }

        if(investZeroError === false && this.item.plan_deviation_skip_approval === false) {
          this.item.deviation_outside_margin = null
        }
      }  
      this.$forceUpdate()
    },
    addPreviousContract(contract) {
      this.item.previous_contract = contract.id
      this.item.previous = contract
      this.$refs.addPreviousContractModel.hide()
    },
    setPreviousContract(fromNewCustomer = false) {
      if (this.item.customer_external_id !== null && this.item.contract_year !== '' && this.item.type !== null) {
        var params = {
          customer_external_id: this.item.customer_external_id,
          contract_year: this.item.contract_year,
          type: this.item.type
        }
        let url = '/api/sandoz/educational/contracts/get-previous-contract'
        axios.post(url, params)
          .then((response) => {
            const self = this
            let previousContract = response.data.previousContract
            if (previousContract) {
              self.item.previous_contract = previousContract.id
              self.item.previous = previousContract
            } else {
              self.item.previous_contract = null
              self.item.previous = null
            }
          })
      }
    },
    checkLangSelection() {
      if ('id' in this.item) {
        return this.item.language !== '' ? true : false
      } else {
        return this.item.language !== '' && this.setLang === true ? true : false
      }
    },
    setMasterAddress() {
      this.addressMode = 'master'
      this.$refs.addressSearch.showAddressSearchModal(this)
    },
    addressSelected(addressEvent) {
      let address = addressEvent.address
      let multipleContractsPerPractice = addressEvent.multipleContractsPerPractice

      // set external id
      let externalId = multipleContractsPerPractice ? address.external_id : address.org_external_id
      this.item.customer_external_id = externalId

      if (this.item.addresses.length > 0) {
        let currentAdr = this.item.addresses[0]
        currentAdr.customer_no_xpris = this.item.customer_external_id
        currentAdr.last_name = address.person_name
        currentAdr.company_name = address.org_full_name
        currentAdr.street = address.street
        currentAdr.zip = address.zip_code
        currentAdr.city = address.city
      } else {
        var addressObj = {
          address_type: 'master',
          customer_no_xpris: this.item.customer_external_id,
          last_name: address.person_name,
          company_name: address.org_full_name,
          street: address.street,
          zip: address.zip_code,
          city: address.city,
          items: []
        }
        this.item.addresses.push(addressObj)
      }
      this.addItemsToAddresses(false)
      this.setContractDateToItems()
      this.loadplanData()
    },
    getAddressItemData() {
      var url = `/api/sandoz/educational/contracts/detail/${this.$route.params.id}`
      axios
        .get(url)
        .then((response) => {
          var item = response
          this.item.addresses = item.data.addresses
          this.$nextTick(() => {
            this.loaded = true
            this.addItemsToAddresses(false)
            this.setContractNo()
            this.setSelectedItemPrice()
            this.itemOld = {...this.item}
            this.setContractDateToItems()
            this.enableWatchers()
            this.$nextTick(() => {
              this.setDefaultPrice()
              this.setAmountNeed()
              this.saving = false
            })
          })
        })
    },
    setWithoutWatcher(propertyName, value) {
      if (this.watcherWorking || !this.loaded) {
        return
      }
      this.watcherWorking = true
      this.item[propertyName] = value
      this.$nextTick(() => {
        this.watcherWorking = false
      })
    },
    setWholesaler(address, owningComponent) {
      if (!address.org_wholesalers) {
        return
      }
      let wholesalers = address.org_wholesalers.trim().split(';').map(i => i.trim())

      owningComponent.item.wholesaler_1_id = null
      owningComponent.item.wholesaler_2_id = null
      owningComponent.item.wholesaler_3_id = null
      owningComponent.item.wholesaler_4_id = null
      owningComponent.item.wholesaler_5_id = null

      wholesalers.forEach((wholesaler, wholesalerIndex) => {
        let wholesalerSelectValues = this.$refs.wholesalerSelect.$children[0].$children[0].selectItems.filter(e => e.dwh_name == wholesaler)
        if (wholesalerSelectValues.length > 0) {
          let wholesalerSelectId = wholesalerSelectValues[0].id
          let wholesalerFieldIndex = wholesalerIndex + 1
          owningComponent.item[`wholesaler_${wholesalerFieldIndex}_id`] = wholesalerSelectId
        }
      })
    },
    isReadonly(line, q, child = false) {
      let field = null
      let qStartMonth = null
      let qEndMonth = null
      let assinable = null

      switch (q) {
        case 'q1':
          field = 'user_date_q1_input'
          qStartMonth = 1
          qEndMonth = 3
          assinable = 'q1_assignable'
          break
        case 'q2':
          field = 'user_date_q2_input'
          qStartMonth = 4
          qEndMonth = 6
          assinable = 'q2_assignable'
          break
        case 'q3':
          field = 'user_date_q3_input'
          qStartMonth = 7
          qEndMonth = 9
          assinable = 'q3_assignable'
          break
        case 'q4':
          field = 'user_date_q4_input'
          qStartMonth = 10
          qEndMonth = 12
          assinable = 'q4_assignable'
          break
        case 'yearly':
          field = 'user_date_yearly_input'
          break
      }

      if (q === 'yearly') {
        return !(!!line[field] && (line.delivery && line.delivery[q] ? !line.delivery[q].is_delivery_executed : true))
      }

      let validFromMonth = null
      if (line.valid_from_month) {
        validFromMonth = line.valid_from_month
      }
      let validToMonth = null
      if (line.valid_to_month) {
        validToMonth = line.valid_to_month
      }

      var readonlyVal = !(!!line[field] && (!validFromMonth || validFromMonth <= qEndMonth) && (validToMonth && validToMonth >= qStartMonth) &&
        (line.delivery && line.delivery[q] ? !line.delivery[q].is_delivery_executed : true))

      var sync = line.sync_quater_with_children
      if (sync && !readonlyVal && q !== 'yearly' && child === true) {
        if (line[assinable] === 1) {
          return false
        } else {
          line['sync_quarter_' + q] = true
          return true
        }
      }
      return readonlyVal
    },
    setDateToChildren(line, i, type = 'from') {
      const self = this
      self.$nextTick(function () {
        if (!!line.allow_date_to_set_children) {
          if (line.children && Object.keys(line.children).length) {
            line.children.forEach(function (subLine) {
              if (type === 'from') {
                subLine.valid_from = line.valid_from
                subLine.valid_from_month = line.valid_from_month
                subLine.valid_from_year = line.valid_from_year
              } else {
                subLine.valid_to = line.valid_to
                subLine.valid_to_month = line.valid_to_month
                subLine.valid_to_year = line.valid_to_year
              }

              if (subLine.delivery && Object.keys(subLine.delivery).length) {
                Object.keys(subLine.delivery).forEach(function (p) {
                  if ((subLine.delivery[p] && subLine.delivery[p].quantity > 0) && self.isReadonly(subLine, p)) {
                    subLine.delivery[p].quantity = null
                  }
                })
              }

              self.setPrice(subLine, i)

              if (!!subLine.allow_date_to_set_children) {
                if (subLine.children && Object.keys(subLine.children).length) {
                  subLine.children.forEach(function (childLine) {
                    if (type === 'from') {
                      childLine.valid_from = subLine.valid_from
                      childLine.valid_from_month = subLine.valid_from_month
                      childLine.valid_from_year = subLine.valid_from_year
                    } else {
                      childLine.valid_to = subLine.valid_to
                      childLine.valid_to_month = subLine.valid_to_month
                      childLine.valid_to_year = subLine.valid_to_year
                    }

                    if (childLine.delivery && Object.keys(childLine.delivery).length) {
                      Object.keys(childLine.delivery).forEach(function (p) {
                        if ((childLine.delivery[p] && childLine.delivery[p].quantity > 0) && self.isReadonly(childLine, p)) {
                          childLine.delivery[p].quantity = null
                        }
                      })
                    }

                    self.setPrice(childLine, i)
                  })
                }
              }
            })
          }
        }
        if (line.delivery && Object.keys(line.delivery).length) {
          Object.keys(line.delivery).forEach(function (p) {
            if ((line.delivery[p] && line.delivery[p].quantity > 0) && self.isReadonly(line, p)) {
              line.delivery[p].quantity = null
            }
          })
        }
        self.setPrice(line, i)
      })
    },
    setDateChanged(line, type) {
      this.$nextTick(function () {
        if (!!line.allow_date_to_set_children) {
          if (line.children && Object.keys(line.children).length) {
            line.children.forEach(function (subLine) {
              if (type === 'from') {
                subLine.valid_from_changed = true
              } else {
                subLine.valid_to_changed = true
              }

              if (!!subLine.allow_date_to_set_children) {
                if (subLine.children && Object.keys(subLine.children).length) {
                  subLine.children.forEach(function (childLine) {
                    if (type === 'from') {
                      childLine.valid_from_changed = true
                    } else {
                      childLine.valid_to_changed = true
                    }
                  })
                }
              }
            })
          }
        }

        if (type === 'from') {
          line.valid_from_changed = true
        } else {
          line.valid_to_changed = true
        }
      })
    },
    setQuantityToParent(line, subLine, q, i) {
      const self = this
      this.$nextTick(function () {
        if (!!subLine.write_qty_from_child_to_parent) {
          if (line.delivery) {
            line.delivery[q].quantity = subLine.delivery ? subLine.delivery[q].quantity : null
            self.setPrice(line, i, q)
          }
        }
        self.setPrice(subLine, i, q)
      })
    },
    setPrice(itm, index = -1, field = null, fromload = 0) {
      const self = this

      const price = self.calculatePrice(itm, index)
      if (self.activeTab === 1 && price.maxQunatityError && field !== null) {
        self.$notify({
          title: this.$i18n.t('core.validation_error'),
          text: this.$i18n.t('sandoz.max_quantity_exceed'),
          type: 'error'
        })

        itm.delivery[field].quantity = itm.delivery[field].previous_quantity
        return false
      } else {
        itm.price = price.price
        itm.price_changes = price.price_changes
        self.$nextTick(function () {
          self.setSelectedItemPrice()
        })
      }
    },
    setDefaultPrice() {
      var _self = this
      _self.$nextTick(function () {
        if (_self.item.addresses) {
          _self.item.addresses.forEach(function (address) {
            if (address.address_type !== 'master') {
              return false
            }

            if (address.items.length) {
              address.items.forEach(function (item) {
                item.price = item.loaded_price ? Math.round(item.loaded_price) : null
                if (item.children && Object.keys(item.children).length) {
                  item.children.forEach(function (subItem) {
                    subItem.price = subItem.loaded_price ? Math.round(subItem.loaded_price) : null

                    if (subItem.children && Object.keys(subItem.children).length) {
                      subItem.children.forEach(function (childItem) {
                        childItem.price = childItem.loaded_price ? Math.round(childItem.loaded_price) : null
                      })
                    }
                  })
                }
              })
            }
          })
        }
      })
    },
    setQuantityToChild(itm, qtr, index = -1) {
      const self = this
      self.$nextTick(function () {
        if (itm.children && Object.keys(itm.children).length) {
          itm.children.forEach(function (subLine) {
            if (subLine.delivery && Object.keys(subLine.delivery).length) {
              if ((subLine.delivery[qtr] && subLine.child_qty)) {
                subLine.delivery[qtr].quantity = subLine.child_qty && itm.delivery[qtr].quantity !== null ? subLine.child_qty * itm.delivery[qtr].quantity : null
                self.$nextTick(function () {
                  self.setPrice(subLine, index, qtr)
                })
              }
            }

            if (subLine.children && Object.keys(subLine.children).length) {
              subLine.children.forEach(function (childLine) {
                if (childLine.delivery && Object.keys(childLine.delivery).length) {
                  if ((childLine.delivery[qtr] && childLine.child_qty)) {
                    childLine.delivery[qtr].quantity = childLine.child_qty && itm.delivery[qtr].quantity !== null ? childLine.child_qty * childLine.delivery[qtr].quantity : null
                    self.$nextTick(function () {
                      self.setPrice(childLine, index, qtr)
                    })
                  }
                }
              })
            }
          })
        }
      })
    },
    isGadgetOrBox(item) {
      const self = this
      let is = false
      self.$nextTick(function () {
        if (item.children && Object.keys(item.children).length) {
          item.children.forEach(function (subLine) {
            if (subLine.box_or_gadget === 'box') {
              is = true
            }

            if (subLine.children && Object.keys(subLine.children).length) {
              subLine.children.forEach(function (childLine) {
                if (childLine.box_or_gadget === 'gadget') {
                  is = true
                }
              })
            }
          })
        }
      })
      return is
    },
    setBoxQuantity(item, qtr, boxOrGadget, index) {
      const self = this
      if (!['box', 'gadget'].includes(boxOrGadget)) {
        return
      }

      self.$nextTick(function () {
        let boxes = 0
        let gadgets = {yearly: 0, q1: 0, q2: 0, q3: 0, q4: 0}
        let itemBoxes = null
        let gadgetBoxes = null
        if (item.children && Object.keys(item.children).length) {
          item.children.forEach(function (subLine) {
            if (subLine.box_or_gadget === 'box' && subLine.delivery && Object.keys(subLine.delivery).length) {
              if ((subLine.delivery.yearly && subLine.delivery.yearly.quantity > 0)) {
                boxes = boxes + parseInt(subLine.delivery.yearly.quantity)
              }
            }

            if (subLine.box_or_gadget === 'gadget' && subLine.delivery && Object.keys(subLine.delivery).length) {
              Object.keys(subLine.delivery).forEach(function (p) {
                if ((subLine.delivery[p] && subLine.delivery[p].quantity > 0)) {
                  gadgets[p] = gadgets[p] + parseInt(subLine.delivery[p].quantity)
                }
              })
            }

            if (subLine.children && Object.keys(subLine.children).length) {
              subLine.children.forEach(function (childLine) {
                if (childLine.box_or_gadget === 'gadget' && childLine.delivery && Object.keys(childLine.delivery).length) {
                  Object.keys(childLine.delivery).forEach(function (p) {
                    if ((childLine.delivery[p] && childLine.delivery[p].quantity > 0)) {
                      gadgets[p] = gadgets[p] + parseInt(childLine.delivery[p].quantity)
                    }
                  })
                }

                if (childLine.box_or_gadget === 'box' && childLine.delivery && Object.keys(childLine.delivery).length) {
                  if ((childLine.delivery.yearly && childLine.delivery.yearly.quantity > 0)) {
                    boxes = boxes + parseInt(childLine.delivery.yearly.quantity)
                  }
                }
              })
            }
          })
        }

        if (boxOrGadget === 'box') {
          Object.keys(item.delivery).forEach(function (p) {
            if ((item.delivery[p])) {
              gadgetBoxes = gadgets[p] ? gadgets[p] / 20 : null
              gadgetBoxes = gadgetBoxes ? gadgetBoxes.toString().split(".")[0] : null

              if (boxes < gadgetBoxes) {
                itemBoxes = boxes ? parseInt(boxes) : null
              } else {
                itemBoxes = gadgetBoxes ? parseInt(gadgetBoxes) : null
              }

              item.delivery[p].quantity = itemBoxes > 0 ? itemBoxes : null
              self.setPrice(item, index, qtr)
            }
          })
        } else if (boxOrGadget === 'gadget') {
          gadgetBoxes = gadgets[qtr] ? gadgets[qtr] / 20 : null
          gadgetBoxes = gadgetBoxes ? gadgetBoxes.toString().split(".")[0] : null

          if (boxes < gadgetBoxes) {
            itemBoxes = boxes ? parseInt(boxes) : null
          } else {
            itemBoxes = gadgetBoxes ? parseInt(gadgetBoxes) : null
          }

          if (item.delivery[qtr]) {
            item.delivery[qtr].quantity = itemBoxes > 0 ? itemBoxes : null
            self.setPrice(item, index, qtr)
          }
        }
      })
    },
    calculatePrice(itm, index) {
      const self = this
      let price = null
      let priceChanges = {}
      let capBasePrice = null
      let adjustablePrice = 'yes'
      const address = self.item.addresses[index === -1 ? self.addressIndex : index]
      if (itm.base_price && Object.keys(itm.base_price).length) {
        itm.base_price.forEach(function (p) {
          if (p.sales_line_price_category && Object.keys(p.sales_line_price_category).length) {
            if (p.price && p.sales_line_price_category.price_category === self.item.type) {
              price = p.price
              capBasePrice = p.sales_line_price_category
              adjustablePrice = p.adjustable_price
            }
            priceChanges = p.price_changes
            return false
          } else if (p.price_changes) {
            priceChanges = p.price_changes
            return false
          }
        })
      }

      address.cap_base_price = capBasePrice

      let quantity = null
      let yearlyQuantity = null
      if (itm.delivery && Object.keys(itm.delivery).length) {
        Object.keys(itm.delivery).forEach(function (p) {
          if ((itm.delivery[p] && itm.delivery[p].quantity <= 0) || (!itm.delivery[p].is_delivery_executed && self.isReadonly(itm, p)) ||
            (itm.box_or_gadget && !self.isGadgetOrBox(itm))
          ) {
            return false
          }

          if (itm.delivery[p].quantity) {
            quantity = quantity + parseInt(itm.delivery[p].quantity)

            if (p === 'yearly') {
              yearlyQuantity = parseInt(itm.delivery[p].quantity)
            }
          }
        })
      }

      let maxQunatityError = false
      if ((itm.max_quantity !== '' || itm.max_quantity !== null) && itm.max_quantity > 0) {
        if (parseInt(itm.max_quantity) < quantity) {
          maxQunatityError = true
        }
      }

      let newPrice = null
      if (priceChanges && priceChanges.price_changes && priceChanges.price_changes === 'any_price_allowed') {
        newPrice = itm.price * 1
      } else if (quantity && price) {
        if (itm.apo_one_multiply_with_quantity && yearlyQuantity > 0) {
          quantity = (parseInt(quantity) - yearlyQuantity) * yearlyQuantity
        }

        if (adjustablePrice === 'yes') {
          let months = ((itm.valid_to_month - itm.valid_from_month) + 1) / 12
          let durationPrice = (price * months) * yearlyQuantity

          let qPrice = price * (parseInt(quantity) - yearlyQuantity)
          newPrice = parseFloat((durationPrice + qPrice)).toFixed(2)
        } else {
          newPrice = parseFloat(price * quantity).toFixed(2)
        }
      }

      // Set for fix value items only
      if (itm.product_master_values && Object.keys(itm.product_master_values).length) {
        newPrice = Math.round(newPrice / 10) * 10
      }

      return {
        'price': newPrice ? Math.round(newPrice) : null,
        'price_changes': priceChanges ? priceChanges : {},
        'maxQunatityError': maxQunatityError
      }
    },
    setContractDateToItems(fromload = 0) {
      const self = this
      const startDate = moment(self.item.contract_start)
      const expiredDate = moment(self.item.contract_expiration)

      let date = null
      let startMonth = null
      let startYear = null
      let endDate = null
      let endMonth = null
      let endYear = null
      if (startDate.isValid()) {
        date = startDate.date()
        startMonth = startDate.month() + 1
        startYear = startDate.year()
      }

      if (expiredDate.isValid()) {
        endDate = expiredDate.date()
        endMonth = expiredDate.month() + 1
        endYear = expiredDate.year()
      }

      startMonth = (startMonth < 10 ? '0' + startMonth : startMonth).toLocaleString()
      endMonth = (endMonth < 10 ? '0' + endMonth : endMonth).toLocaleString()

      if (self.item.addresses) {
        self.item.addresses.forEach(function (address, index) {
          if (address.address_type !== 'master') {
            return false
          }

          if (address.items) {
            address.items.forEach(function (line) {
              if (line.children) {
                line.children.forEach(function (subLine) {
                  if (subLine.children) {
                    subLine.children.forEach(function (childLine) {
                      childLine.valid_from_month = startYear != childLine.valid_from_year ? 1 : (childLine.valid_from_month != startMonth ? startMonth : childLine.valid_from_month)
                      childLine.valid_from_year = startYear != childLine.valid_from_year ? startYear : childLine.valid_from_year

                      childLine.valid_to_month = endYear != childLine.valid_to_year ? 12 : (childLine.valid_to_month != endMonth ? endMonth : childLine.valid_to_month)
                      childLine.valid_to_year = endYear != childLine.valid_to_year ? startYear : childLine.valid_to_year

                      self.$nextTick(function () {
                        if (fromload === 0) {
                          self.setPrice(childLine, index)
                        }
                      })
                    })
                  }

                  subLine.valid_from_month = startYear != subLine.valid_from_year ? 1 : (subLine.valid_from_month != startMonth ? startMonth : subLine.valid_from_month)
                  subLine.valid_from_year = startYear != subLine.valid_from_year ? startYear : subLine.valid_from_year

                  subLine.valid_to_month = endYear != subLine.valid_to_year ? 12 : (subLine.valid_to_month != endMonth ? endMonth : subLine.valid_to_month)
                  subLine.valid_to_year = endYear != subLine.valid_to_year ? startYear : subLine.valid_to_year

                  self.$nextTick(function () {
                    if (fromload === 0) {
                      self.setPrice(subLine, index)
                    }
                  })
                })
              }

              line.valid_from_month = startYear != line.valid_from_year ? 1 : (line.valid_from_month != startMonth ? startMonth : line.valid_from_month)
              line.valid_from_year = startYear != line.valid_from_year ? startYear : line.valid_from_year

              line.valid_to_month = endYear != line.valid_to_year ? 12 : (line.valid_to_month != endMonth ? endMonth : line.valid_to_month)
              line.valid_to_year = endYear != line.valid_to_year ? startYear : line.valid_to_year
              self.$nextTick(function () {
                if (fromload === 0) {
                  self.setPrice(line, index, null, fromload)
                }
              })
            })
          }
        })
      }
    },
    validatePrice(itm, index) {
      const self = this
      const basePrice = self.calculatePrice(itm, index)
      if (basePrice.price_changes && basePrice.price_changes.price_changes === 'no_changes_allowed') {
        this.$notify({
          title: this.$i18n.t('core.validation_error'),
          text: this.$i18n.t('sandoz.no_changes_allowed_error'),
          type: 'error'
        })
        itm.price = basePrice.price
        return false
      } else if (basePrice.price_changes && basePrice.price_changes.price_changes === 'minus_20_allowed') {
        const minPrice = parseFloat(((80 / 100) * basePrice.price).toFixed(0))
        if (itm.price < minPrice) {
          this.$notify({
            title: this.$i18n.t('core.validation_error'),
            text: this.$i18n.t('sandoz.minus_20_allowed_error'),
            type: 'error'
          })
          itm.price = minPrice
          return false
        }

        if (itm.price > basePrice.price) {
          this.$notify({
            title: this.$i18n.t('core.validation_error'),
            text: this.$i18n.t('sandoz.only_downward_error'),
            type: 'error'
          })
          itm.price = basePrice.price
          return false
        }
      } else if (basePrice.price_changes && basePrice.price_changes.price_changes === 'changes_allowed') {
        if (itm.price > basePrice.price) {
          this.$notify({
            title: this.$i18n.t('core.validation_error'),
            text: this.$i18n.t('sandoz.changes_allowed_error'),
            type: 'error'
          })
          itm.price = basePrice.price
          return false
        }
      } else if ((basePrice.price_changes && basePrice.price_changes.price_changes === 'any_price_allowed') &&
        (itm.price === null || itm.price <= 0)
      ) {
        self.$notify({
          title: this.$i18n.t('core.validation_error'),
          text: this.$i18n.t('sandoz.any_price_allowed_error'),
          type: 'error'
        })
        itm.price = basePrice.price
        return false
      }
    },
    setSelectedItemPrice() {
      const self = this
      let price = 0
      let countAddressCapExceed = 0
      self.item.addresses.forEach(function (address, key) {
        let addressPrice = 0
        if (address.address_type === 'master') {
          address.items.forEach(function (item, key1) {
            let masterLinePrice = item.price > 0 ? item.price : 0
            price = parseFloat(price) + parseFloat(masterLinePrice)
            addressPrice = addressPrice + parseFloat(masterLinePrice)

            if (item.children && Object.keys(item.children).length) {
              let childrenPrice = self.getChildSelectedItemPrice(item)
              price = parseFloat(price) + parseFloat(childrenPrice)
              addressPrice = addressPrice + parseFloat(childrenPrice)
            }
          })

          if (address.cap_base_price) {
            let exceedCap = address.cap_base_price.cap

            if (exceedCap !== null && exceedCap < addressPrice) {
              address.cap_exceeded = true
              countAddressCapExceed = countAddressCapExceed + 1
            } else {
              address.cap_exceeded = false
            }
          }
        }
      })

      self.item.cap_exceeded = false
      if (countAddressCapExceed > 0) {
        self.item.cap_exceeded = true
      }

    },
    getChildSelectedItemPrice(item) {
      let childPrice = 0
      const self = this
      item.children.forEach(function (item, key1) {
        let masterLineChildPrice = item.price > 0 ? item.price : 0
        childPrice = childPrice + parseFloat(masterLineChildPrice)
        if (item.children && Object.keys(item.children).length) {
          let childrenPrice = self.getChildSelectedItemPrice(item)
          childPrice = parseFloat(childPrice) + parseFloat(childrenPrice)
        }
      })
      return childPrice
    },
    addItemsToAddresses(reset = true) {
      const _self = this
      this.$nextTick(function () {
        if (_self.item.addresses) {
          _self.item.addresses.forEach(function (address, index) {
            if (address.address_type !== 'master') {
              return false
            }

            const isDraftOrNew = _self.$route.params.id === 'new' || (_self.item && (_self.item.status === 'Draft' || _self.item.status === 'Feedback'))
            if ((reset && isDraftOrNew) || _self.$route.params.id === 'new') {
              _self.item.addresses[index].items = []
            }

            if (!address.items.length) {
              _self.addItemsToAddress(index, reset)
            }

            if (address.items.length && _self.$route.params.id !== 'new') {
              address.items.forEach(function (item) {
                const itemPrice = _self.addItemPrice(item, index)
                const price = _self.calculatePrice(item, index)

                item.item_price = itemPrice.price
                item.price = price.price
                item.price_changes = price.price_changes

                if (item.children && Object.keys(item.children).length) {
                  item.children.sort((a, b) => a.sort_order - b.sort_order).forEach(function (subItem) {
                    const subItemPrice = _self.addItemPrice(subItem, index)
                    const subPrice = _self.calculatePrice(subItem, index)

                    subItem.item_price = subItemPrice.price
                    subItem.price = subPrice.price
                    subItem.price_changes = subPrice.price_changes

                    if (subItem.children && Object.keys(subItem.children).length) {
                      subItem.children.sort((a, b) => a.sort_order - b.sort_order).forEach(function (childItem) {
                        const childItemPrice = _self.addItemPrice(childItem, index)
                        const childPrice = _self.calculatePrice(childItem, index)

                        childItem.item_price = childItemPrice.price
                        childItem.price = childPrice.price
                        childItem.price_changes = childPrice.price_changes
                      })
                    }
                  })
                }
              })
            }
          })
        }
      })
    },
    addItemsToAddress(index) {
      const _self = this
      if (!this.item.type) {
        return
      }

      if (_self.addItemsToAddressFlag && _self.addItemsToAddressFlagIndex === index) {
        return
      }

      _self.addItemsToAddressFlag = true
      _self.addItemsToAddressFlagIndex = index

      let url = '/api/sandoz/educational/items?c_type=' + _self.item.type + '&c_start=' + _self.item.contract_start + '&c_end=' + _self.item.contract_expiration + '&c_lang=' + _self.item.language + '&c_year=' + _self.item.contract_year
      axios
        .get(url)
        .then((response) => {
          response.data.forEach(function (item) {
            const itemPrice = _self.addItemPrice(item, index)
            const price = _self.calculatePrice(item, index)

            item.item_price = itemPrice.price
            item.price = price.price
            item.price_changes = price.price_changes

            if (item.children && Object.keys(item.children).length) {
              item.children.sort((a, b) => a.sort_order - b.sort_order).forEach(function (subItem) {
                const subItemPrice = _self.addItemPrice(subItem, index)
                const subPrice = _self.calculatePrice(subItem, index)

                subItem.item_price = subItemPrice.price
                subItem.price = subPrice.price
                subItem.price_changes = subPrice.price_changes

                if (subItem.children && Object.keys(subItem.children).length) {
                  subItem.children.sort((a, b) => a.sort_order - b.sort_order).forEach(function (childItem) {
                    const childItemPrice = _self.addItemPrice(childItem, index)
                    const childPrice = _self.calculatePrice(childItem, index)

                    childItem.item_price = childItemPrice.price
                    childItem.price = childPrice.price
                    childItem.price_changes = childPrice.price_changes
                  })
                }
              })
            }

            _self.item.addresses[index].items.push(item)
          })
          _self.addItemsToAddressFlag = false
          _self.addItemsToAddressFlagIndex = null
        })
    },
    addItemPrice(itm, index = -1) {
      const self = this
      let itemPrice = null
      let priceChanges = {}
      const address = self.item.addresses[index === -1 ? self.addressIndex : index]
      if (itm.base_price && Object.keys(itm.base_price).length) {
        itm.base_price.forEach(function (p) {
          if (p.sales_line_price_category && Object.keys(p.sales_line_price_category).length) {
            if (p.sales_line_price_category.price_category === self.item.type) {
              if (p.price) {
                itemPrice = p.price
              }
              priceChanges = p.price_changes
              return false
            } else if (p.price_changes) {
              priceChanges = p.price_changes
              return false
            }
          } else if (p.price_changes) {
            priceChanges = p.price_changes
            return false
          }
        })
      }

      return {'price': itemPrice, 'price_changes': priceChanges ? priceChanges : {}}
    },
    pdfContract() {
      this.$root.$loading.start()

      let documentName = this.item.contract_no
      axios.post('/api/sandoz/generate-pdf2', {id: this.$route.params.id}, {responseType: 'arraybuffer'})
        .then((response) => {
          require('downloadjs')(
            response.data,
            documentName + '.pdf',
            'application/pdf'
          )
          this.$root.$loading.finish()
        }).catch((error) => {
        this.$root.$loading.finish()
        console.log(error)
      })
    },
   confirmWithUser() {
      const self = this
      var diffObject = diff(self.itemOld, self.item)
      delete diffObject['cap_exceeded']
      delete diffObject['sales_line']
      console.log("diffObject", diffObject)
      if((self.item.status === 'Draft' || self.item.status === 'Feedback') && (self.item.save_before_pdf_download || (diffObject && Object.keys(diffObject).length))) {
        self.$bvModal.msgBoxConfirm('Some changes are not saved!', {
            title: 'Save changes',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'primary',
            okTitle: 'Save & Continue',
            cancelTitle: 'Cancel',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true
          })
        .then(value => {
          if(value) {
            self.downloadPDF = true
            self.item.is_pdf_download = true
            self.save()
          }
        })
        .catch(err => {
          console.log('error', err)
          // An error occurred
        })
      } else {
        self.pdfContract()
      }
    },
    toggleAccordion(address, item) {
      const index = this.getAccordion(address, item)
      if (index !== -1) {
        this.accordionItems.splice(index, 1);
      } else {
        this.accordionItems.push(address + '' + item)
      }
    },
    getAccordion(address, item) {
      return this.accordionItems.indexOf(address + '' + item)
    },
    showDuplicateContract() {
      const routeData = this.$router.resolve({path: '/sandoz/educational/contracts/' + this.showContract})
      window.open(routeData.href, '_blank')
    },
    getQuaterFromMonth(month) {
      if (month >= 1 && month <= 3) {
        return 1
      } else if (month > 3 && month <= 6) {
        return 2
      } else if (month > 6 && month <= 9) {
        return 3
      } else if (month > 9 && month <= 12) {
        return 4
      }
      return null
    },
    checkLineChildren(line) {
      if (line.children && Object.keys(line.children).length) {
        return true
      }
      return false
    },
    linkValidate(link) {
      var linkUpdated = (link.indexOf('://') === -1) ? '//' + link : link;
      return linkUpdated
    },
    setPreviousQuantity(value, itm, field = null) {
      if(itm.delivery[field]) {
        itm.delivery[field].previous_quantity = value
      }
    },
    openPictureOverlay (line) {
      const self = this
      self.pictures = []
      self.pictures.push({
        src: '/storage/product/' + line.product.product_master.picture.path,
        alt: line.product.product_master.picture.name
      })
      self.$refs.imageSilentBox.openOverlay(self.pictures[0], 0)
    },
    setActualEducation(item) {
      var params = {
        item: this.item
      }

      if(this.checkPlannedWithActualAmount()) {
        this.$notify({
          title: this.$i18n.t('core.validation_error'),
          text: this.$i18n.t('core.validation_error_text', { message: this.$i18n.t('sandoz.actual_education_amount_less_planned_educational') }),
          type: 'error'
        })
        return
      }

      if(this.checkAmount()) {
        this.$notify({
          title: this.$i18n.t('core.validation_error'),
          text: this.$i18n.t('core.validation_error_text', { message: this.$i18n.t('sandoz.actual_amount_exceed') }),
          type: 'error'
        })
        return
      }


      let url = '/api/sandoz/educational/contracts/set-actual-education'
        axios.post(url, params)
        .then((response) => {
            if(response.data.success === true) {
              this.$notify({
                title: this.$i18n.t('core.saved'),
                text: this.$i18n.t('core.saved_text')
              })
            }
        })
    },
    setTitleItems () {
      if(this.item.language === 'DE') {
        this.titleItems = [
          { value: null, text: 'Partnertitel auswählen' },
          { value: 'Inhaber', text: 'Inhaber'},
          { value: 'Inhaberin', text: 'Inhaberin' },
          { value: 'Dr. med.', text: 'Dr. med.'},
          { value: 'Geschäftsführer', text: 'Geschäftsführer'},
          { value: 'Geschäftsführerin', text: 'Geschäftsführerin'},
          { value: 'FMH Allgemeine Innere Medizin', text: 'FMH Allgemeine Innere Medizin'}
        ]
      } else if(this.item.language === 'FR') {
        this.titleItems = [
          { value: null, text: 'Sélectionnez le titre du partenaire' },
          { value: 'Propriétaire', text: 'Propriétaire'},
          { value: 'Pharmacienne responsable', text: 'Pharmacienne responsable' },
          { value: 'Pharmacien responsable', text: 'Pharmacien responsable'},
          { value: 'Pharmacienne', text: 'Pharmacienne'},
          { value: 'Pharmacien', text: 'Pharmacien'},
          { value: 'Dr. med.', text: 'Dr. med.'}
        ]

      } else if(this.item.language === 'IT') {
        this.titleItems = [
          { value: null, text: 'Seleziona il titolo del partner' },
          { value: 'Gerente', text: 'Gerente'},
          { value: 'Proprietario', text: 'Proprietario' },
          { value: 'Proprietaria', text: 'Proprietaria'},
          { value: 'Dr. med.', text: 'Dr. med.'}
        ]
      }
    },
    setTitle (i) {
      console.log(i)
      this.item['contract_partner_'+i+'_title'] = this.item['contract_partner_'+i+'_title_dropdown']
    },
    quantityMissing() {
      let _self = this
      let quantityZero = true
      if (this.item.addresses) {
        _self.item.addresses.forEach(function (address) {
          if (address.address_type !== 'master') {
            return false
          }

          address.items.forEach(function (line) {
            if (line.delivery && Object.keys(line.delivery).length) {
              Object.keys(line.delivery).forEach(function (p) {
                if(line.product && line.product.product_master && line.product.product_master.hold_actual_amount_of_educational === true) {
                  return false
                }

                if (line.delivery[p] && line.delivery[p].quantity > 0) {
                  quantityZero = false
                  return false
                }
              })
            }

            if (line.children && Object.keys(line.children).length) {
              line.children.forEach(function (subLine) {
                if (subLine.delivery && Object.keys(subLine.delivery).length) {
                  Object.keys(subLine.delivery).forEach(function (p) {
                    if (subLine.delivery[p] && subLine.delivery[p].quantity > 0) {
                      quantityZero = false
                      return false
                    }
                  })
                }

                if (subLine.children && Object.keys(subLine.children).length) {
                  subLine.children.forEach(function (childLine) {
                    if (childLine.delivery && Object.keys(childLine.delivery).length) {
                      Object.keys(childLine.delivery).forEach(function (p) {
                        if (childLine.delivery[p] && childLine.delivery[p].quantity > 0) {
                          quantityZero = false
                          return false
                        }
                      })
                    }
                  })
                }
              })
            }
          })
        })
        return quantityZero
      }
    },
    checkPlannedAmount() {
      if(!this.item.amount_of_educational || !this.item.planned_amount_of_educational) {
        return false
      }
      return parseInt(this.item.planned_amount_of_educational) > parseInt(this.item.amount_of_educational) ? true : false 
    },
    checkPlannedWithActualAmount() {
      if(!this.item.actual_amount_of_educational || !this.item.planned_amount_of_educational) {
        return false
      }

      return parseInt(this.item.planned_amount_of_educational) < parseInt(this.item.actual_amount_of_educational) ? true : false 
    },
    loadplanData() {
      if(this.item.contract_year && this.item.type && this.item.customer_external_id && (this.item.status === 'Draft' || this.item.status === 'Feedback')) {
        let url = '/api/loadplanData?type='+this.item.type+'&year='+this.item.contract_year+'&external_id='+this.item.customer_external_id
        axios
          .get(url)
          .then((response) => {
            var planData = response.data.planData
            this.planData = planData
            if(planData) {
              this.item.plan_target_invest = planData.target_invest !== null ? this.calculatePriceBasedonDuration(parseInt(planData.target_invest)) : null

              if((this.$route.params.id === 'new' && parseInt(this.item.planned_amount_of_educational) === 0) || parseInt(this.item.planned_amount_of_educational) === 0) {
                this.item.planned_amount_of_educational = parseInt(planData.target_invest)
              }
            } else {
              this.item.plan_target_invest = null
              this.item.deviation_outside_margin = null


              if(this.$route.params.id === 'new') {
                this.item.planned_amount_of_educational = 0
              }
            }

            this.$nextTick(() => {
              this.calculatePlanTargetInvest()
              this.setApprovers()
            })
          })
      }
    },
    calculatePlanTargetInvest() {
      var planTargetInvest = null
      if(this.item.plan_target_invest !== 0 && this.item.plan_target_invest !== null){
        planTargetInvest = this.item.plan_target_invest
      }
      var contractTargetInvest = parseInt(this.item.planned_amount_of_educational)
      if(contractTargetInvest !== 0 && planTargetInvest !== null && this.item.sales_line) {
        if(contractTargetInvest > parseInt(planTargetInvest)) {
          var rangePercentage = this.checkDifferenceAndGetPercDeviation(contractTargetInvest)
          if(rangePercentage !== null && rangePercentage !== 0 && rangePercentage !== 0.00) {
            this.plan_invest_condition = true
            var maxDeviationValue = parseInt(planTargetInvest) + (parseInt(planTargetInvest)*rangePercentage)
            if(parseFloat(contractTargetInvest) > parseFloat(maxDeviationValue)) {
              this.item.plan_deviation_skip_approval = false
              this.plan_invest_outside_range = true
              return 'text-danger';
            } else {
              this.plan_invest_outside_range = null
              return 'text-muted';
            }
          }
        } else {
          this.plan_invest_condition = false
          this.plan_invest_outside_range = null
          return 'text-muted';
        }
      } else {
        this.plan_invest_outside_range = null
      }
      return 'text-muted';
    },
    checkDifferenceAndGetPercDeviation(val) {
      if(this.item.sales_line) {
        if(val > 0 && val <= 10000) {
          return this.item.sales_line.deviation_range_1
        } else if(val > 10000 && val <= 20000) {
          return this.item.sales_line.deviation_range_2
        } else if(val > 20000) {
          return this.item.sales_line.deviation_range_3
        }
      }
      return null
    },
    calculatePriceBasedonDuration(val) {
      // if(val !== null && val !== 0 && val !== 0.00 && this.item && this.$route.params.id === 'new') {
      //   var contractStart = this.item.contract_start
      //   var contractExpiration = this.item.contract_expiration
      //   contractStart = contractStart === null ? 12 : parseInt(moment(contractStart).format('MM'))
      //   contractExpiration = contractExpiration === null ? 12 : parseInt(moment(contractExpiration).format('MM'))
      //   var durationVal = (val / 12) * ((contractExpiration - contractStart) + 1)
      //   return parseInt(durationVal)
      // }
      return val
    },
    formatDescription(value) {
      if(value) {
        return this.$options.filters.formatInteger(value);
      }
      return null
    },
    checkPlanDeviationSkipApproval() {
      if((this.item.sales_line && this.item.sales_line.automatic_approval_allowed === true)
      && this.planData !== null && (
      (this.plan_invest_condition === true ) &&
      (this.plan_invest_outside_range !== true)
      )
      ){
        return true
      }
      return false
    },
    setSalesLine () {
    var url = `/api/sandoz/sales_lines?filter[sales_line]=${this.item.type}`
    axios
      .get(url)
      .then((response) => {
        if(response.data.data && '0' in response.data.data) {
          this.item.sales_line = response.data.data[0]
          this.$nextTick(() => {
            this.calculatePlanTargetInvest()
          })
        }
      })
    },
    startVerification() {
      if(this.item.accountholder_email === '' || this.item.accountholder_email === null) {
        this.$notify({
          title: this.$i18n.t('core.validation_error'),
          text: this.$i18n.t('core.validation_error_text', { message: this.$i18n.t('sandoz.customer_email_required') }),
          type: 'error'
        })
        return
      }

      this.saving = true
      // api call to create customer email with external id
      var params = {
        customer_external_id: this.item.customer_external_id,
        email: this.item.accountholder_email,
        name: this.item.accountholder_name,
        user_id: this.item.user_id,
        locale: this.item.language,
        contract_year: this.item.contract_year
      }
      let url = '/api/sandoz/educational/create-customer'
      axios.post(url, params)
      .then((response) => {
        this.saving = false
        this.$notify({
          title: this.$i18n.t('sandoz.verification_process_started'),
          type: 'success'
        })
        this.checkCustomerLinkWithExternalID()
      })
    },
    checkCustomerLinkWithExternalID() {
      if(this.item.accountholder_email !== '' && this.item.customer_external_id !== null && (this.item.status == 'Approved' || this.item.status == 'Signed')) {  
        // api call to create customer email with external id
      var params = {
        customer_external_id: this.item.customer_external_id,
        email: this.item.accountholder_email
      }

      let url = '/api/sandoz/educational/contracts/check-customer-link-contract'
      axios.post(url, params)
      .then((response) => {
        this.customerCount = response.data.customer_count
      })
      }
    }
  }
}
</script>

<style scoped>
td.exist_error {
  border: 1px solid red;
}

.text-set {
  position: relative;
  top:-25px;
  margin-left: 160px
}
</style>
